import React, { useState, useEffect, useRef, useMemo, useCallback, useDeferredValue } from "react";
import { flushSync } from "react-dom";
import { supabase, isSupabaseReady, ADMIN_EMAIL, MAX_EMAIL, STAFF, staffFromEmail } from './supabase';
import * as RC from './ringcentral';
import { extractRateSheet, mapCategoryToAppKey, encodeForAppKey } from './rateSheetExtractor';
import * as BorrowerAPI from './borrowerApi';
import { ADMIN_PATH, MAX_PATH, C, Ic } from './theme';
import { BrandMark, ComplianceBar, FontLoader, Wordmark } from './brand';
import { Btn, InboundCallToast, LoadingSkeleton } from './ui';
import { ALERT_LEADS_FILTER, DM_CAMPAIGNS, LOANS_INIT, SWAP_HISTORY_MAX, WORKING_LEADS_FILTER, normalizeStage } from './constants';
import { baFromDb, baToDb, expenseFromDb, expenseToDb, expenseToDbSlim, followUpAlertCount, hashPassword, isLegacyPassword, isLoanOwnerColumnError, isoDatePlus, leadFromDb, leadToDb, loadAllRows, loadState, loanFromDb, loanToDb, markLoansOwnerColumnMissing, ownerOf, prospectFromLead, prospectFromLoan, pushCallLog, resetLoansOwnerColumn, saveState, settingsFromDb, settingsToDb, smsFromDb, smsToDb, smsToDbSlim, stripLoanOwner } from './lib';
import { LoginScreen } from './views/LoginScreen';
import { AutoHub, Dashboard, UrgentAlertBanner } from './views/Dashboard';
import { Pipeline } from './views/Pipeline';
import { Inquiry } from './views/Inquiry';
import { Marketing } from './views/Marketing';
import { Expenses } from './views/Expenses';
import { BorrowerPortal } from './views/BorrowerPortal';
import { LoanDetail } from './views/LoanDetail';
import { Settings } from './views/Settings';
import { CallConsole } from './views/CallConsole';
import { InfoPage } from './views/InfoPage';
import { SecondPage } from './views/SecondPage';
import { Outbound } from './views/Outbound';
import { Letters } from './views/Letters';
import { LenderLibrary } from './views/LenderLibrary';
import { ChatDock } from './views/ChatDock';
import { WhiteboardDock } from './views/WhiteboardDock';
import { Calc } from './views/Calc';
import { staffAccess, useStaffPrefs, isPracticeOrUnknown, primeStaffPrefs, loadStaffPrefs, resetStaffPrefs, PracticeBanner } from './access';
import { dealContextFromLoan, resetLibraryChats, useLibraryChat, useReportDealContext } from './libraryChat';
import { resetScenarioSession, resumeScenarioSync } from './scenarioStore';
import { flushWhiteboard, resetWhiteboard, useWhiteboardSync } from './whiteboard';
import { resetContactLogSession } from './contactLog';
import { noteCallEnded, resetCallbacksSession } from './views/Callbacks';
import { OwnershipUiProvider, ownershipUiOn } from './features';


// ═══════ THREE-WAY MERGE HELPERS ═══════
// Two staff logins (Reece + Max), extra tabs/devices and the borrower portal
// all write the same loans + settings rows. So a save is a THREE-WAY merge:
//   BASE   = the server copy this tab last loaded / polled / saved
//   LOCAL  = this tab's state
//   SERVER = a fresh read taken right before the write
// Anything this tab did NOT change since BASE takes the server's value, so a
// save can never roll back someone else's newer edit. What this tab DID change
// wins. Lists merge as sets against BASE: what this side removed stays removed,
// what the other side added since BASE is kept — so a completed task stays
// completed, and a borrower upload or another user's quote entry is never lost.

// Order-insensitive JSON (Postgres jsonb reorders keys); null ≡ missing.
function stableStr(v){
  if(v===undefined||v===null)return"null";
  if(typeof v!=="object")return JSON.stringify(v);
  if(Array.isArray(v))return"["+v.map(stableStr).join(",")+"]";
  var ks=Object.keys(v).filter(function(k){return v[k]!==undefined&&v[k]!==null;}).sort();
  return"{"+ks.map(function(k){return JSON.stringify(k)+":"+stableStr(v[k]);}).join(",")+"}";
}
function seq(a,b){return a===b||stableStr(a)===stableStr(b);}
function isPlainObj(v){return v!==null&&typeof v==="object"&&!Array.isArray(v);}
function keysOf3(a,b,s){var seen=Object.create(null);var out=[];[a,b,s].forEach(function(o){if(isPlainObj(o))Object.keys(o).forEach(function(k){if(!seen[k]){seen[k]=1;out.push(k);}});});return out;}
// a=LOCAL, b=BASE, s=SERVER. `depth` = how many object levels to merge key by
// key when both sides changed the same value; `onArr` merges two changed lists.
function merge3(a,b,s,depth,onArr){
  if(a===b||seq(a,b))return s;          // untouched here → server's (possibly newer) value
  if(s===b||seq(s,b))return a;          // untouched on the server → ours
  if(depth>0&&isPlainObj(a)&&isPlainObj(s)){
    var bo=isPlainObj(b)?b:{};var out={};
    keysOf3(a,bo,s).forEach(function(k){var v=merge3(a[k],bo[k],s[k],depth-1,onArr);if(v!==undefined)out[k]=v;});
    return out;
  }
  if(onArr&&Array.isArray(a)&&Array.isArray(s))return onArr(a,b,s);
  return a;                             // both changed the same scalar → this edit wins
}
// Set merge of a list. With no BASE (brand-new row / never baselined) it is a
// plain union — the old, borrower-safe behaviour.
function merge3Set(adminArr,baseArr,serverArr,keyFn){
  var a=Array.isArray(adminArr)?adminArr:[],s=Array.isArray(serverArr)?serverArr:[];
  var out=[];var seen=Object.create(null);
  if(!Array.isArray(baseArr)){
    a.forEach(function(x){var k=keyFn(x);if(!seen[k]){seen[k]=1;out.push(x);}});
    s.forEach(function(x){var k=keyFn(x);if(!seen[k]){seen[k]=1;out.push(x);}});
    return out;
  }
  var inBase=Object.create(null),inSrv=Object.create(null),inAdmin=Object.create(null);
  baseArr.forEach(function(x){inBase[keyFn(x)]=1;});
  s.forEach(function(x){inSrv[keyFn(x)]=1;});
  a.forEach(function(x){inAdmin[keyFn(x)]=1;});
  // Keep every local entry except ones the other side deleted since BASE
  // (duplicates in the local list are kept as-is)…
  a.forEach(function(x){var k=keyFn(x);if(inBase[k]&&!inSrv[k])return;out.push(x);});
  // …then whatever the other side added since BASE.
  s.forEach(function(x){var k=keyFn(x);if(inBase[k]||inAdmin[k]||seen[k])return;seen[k]=1;out.push(x);});
  return out;
}
function listKey(e){
  if(isPlainObj(e)&&e.epoch!=null)return"e:"+e.epoch+"|"+(e.type||e.kind||e.text||e.product||"");
  return"j:"+stableStr(e);
}
// History lists (scenarioHistory, quoteLog, contactLog, swap marks): set merge,
// then chronological when every entry carries an epoch (they are stored oldest
// → newest and capped from the front).
function merge3List(a,b,s){
  var out=merge3Set(a,Array.isArray(b)?b:[],s,listKey);
  if(out.length>1&&out.every(function(e){return isPlainObj(e)&&typeof e.epoch==="number";}))
    out=out.map(function(e,i){return{e:e,i:i};}).sort(function(x,y){return(x.e.epoch-y.e.epoch)||(x.i-y.i);}).map(function(x){return x.e;});
  return out;
}

// ═══════ SAFE LOANS SAVE ═══════
// Borrower submissions (statements, uploads, counseling, callbacks, portal
// logins, testimonials) are written SERVER-SIDE by /api/borrower/*, and Max can
// edit the same files. A blanket upsert from this tab's (possibly stale) copy
// would overwrite those. So each changed loan is merged with a fresh server
// read first:
//   • statement_data / counseling / portal_logins — written only server-side:
//     the server copy always wins.
//   • docs — merged key by key; a borrower testimonial is never dropped.
//   • uploads / tasks — three-way set merge against the baseline, so an admin
//     removal (task ticked done, document removed) sticks while anything the
//     borrower/Max added since is kept.
//   • activity_log — append-only union (never loses an entry).
//   • every other column (and each key inside follow_ups._wf) — three-way:
//     what this tab changed wins, everything else takes the server's value.
function uploadKey(u){return (u&&u.storagePath)?("p:"+u.storagePath):("n:"+((u&&u.name)||"")+"|"+((u&&(u.date||u.epoch))||""));}
function taskKey(t){return String(t);}
// Activity entries are mostly {date,time,timestamp,text,type} with no epoch.
// Key on the time stamp too — keying on text alone collapsed five portal logins
// into one. Never dedupes WITHIN a list: server copies are added only beyond
// the count the admin copy already holds.
function logKey(e){if(!e)return"";var t=e.epoch||e.timestamp||e.ts||((e.date||"")+" "+(e.time||""));return t+"|"+(e.type||"")+"|"+(e.text||"");}
function logTime(e){
  if(!e)return 0;if(e.epoch)return Number(e.epoch)||0;
  var t=Date.parse(e.timestamp||e.ts||"");if(!isNaN(t))return t;
  t=Date.parse(((e.date||"")+" "+(e.time||"")).trim());return isNaN(t)?0:t;
}
function mergeLog(serverArr,adminArr){
  var out=[];var have=Object.create(null);var srvCount=Object.create(null);
  (Array.isArray(adminArr)?adminArr:[]).forEach(function(e){var k=logKey(e);have[k]=(have[k]||0)+1;out.push(e);});
  (Array.isArray(serverArr)?serverArr:[]).forEach(function(e){var k=logKey(e);srvCount[k]=(srvCount[k]||0)+1;if(srvCount[k]>(have[k]||0))out.push(e);});
  return out.map(function(e,i){return{e:e,i:i,t:logTime(e)};}).sort(function(x,y){return(x.t-y.t)||(x.i-y.i);}).map(function(x){return x.e;});
}
// Pipeline interest level + Booked (src/interest.js, 10/9/26) ride in _wf as
// value + stamp pairs. A change on ONE side is handled by the key-by-key
// merge above (a stale tab's save of another field keeps the other device's
// change). Changed on BOTH sides since BASE → the newer stamp wins, value and
// stamp together — never one side's value with the other side's stamp.
var STAMPED_WF_PAIRS=[["interest","interestAt"],["booked","bookedAt"]];
function mergeStampedWf(out,a,b,s){
  if(!isPlainObj(out)||!isPlainObj(out._wf))return out;
  var wfOf=function(o){return(isPlainObj(o)&&isPlainObj(o._wf))?o._wf:{};};
  var wa=wfOf(a),wb=wfOf(b),ws=wfOf(s);
  var wf=null;
  STAMPED_WF_PAIRS.forEach(function(p){
    var k=p[0],t=p[1];
    var mine=!seq(wa[k],wb[k])||!seq(wa[t],wb[t]);
    var theirs=!seq(ws[k],wb[k])||!seq(ws[t],wb[t]);
    if(!mine||!theirs)return;
    var src=String(ws[t]||"")>String(wa[t]||"")?ws:wa;
    wf=wf||Object.assign({},out._wf);
    [k,t].forEach(function(x){if(src[x]===undefined)delete wf[x];else wf[x]=src[x];});
  });
  return wf?Object.assign({},out,{_wf:wf}):out;
}
// Reimbursements owed to Reece (src/reimbursements.js, 10/10/26) ride in _wf
// as ONE list. Items are never taken out of it (Delete is a soft delete —
// deletedAt), so the merge is a union BY ITEM ID: an item on only one side is
// kept, and when both sides hold it the newer updatedAt wins (a tie keeps
// this tab's). The key-by-key merge above would take one side's whole list
// (another device's new item lost — also when an older bundle saved _wf
// without the key) or, both changed, keep two versions of the same item.
// Self-contained on purpose: tests lift this block out of App.jsx as is.
function mergeReimbList(a,s){
  var out=[];var at=Object.create(null);
  var ms=function(x){var t=Date.parse(x&&x.updatedAt);return isFinite(t)?t:0;};
  (Array.isArray(a)?a:[]).forEach(function(x){if(!isPlainObj(x)||!x.id||at[x.id]!=null)return;at[x.id]=out.length;out.push(x);});
  (Array.isArray(s)?s:[]).forEach(function(x){
    if(!isPlainObj(x)||!x.id)return;
    var i=at[x.id];
    if(i==null){at[x.id]=out.length;out.push(x);return;}
    if(ms(x)>ms(out[i]))out[i]=x;
  });
  return out;
}
function mergeReimbWf(out,a,s){
  if(!isPlainObj(out)||!isPlainObj(out._wf))return out;
  var wfOf=function(o){return(isPlainObj(o)&&isPlainObj(o._wf))?o._wf:{};};
  var ra=wfOf(a).reimbursements,rs=wfOf(s).reimbursements;
  if(!Array.isArray(ra)&&!Array.isArray(rs))return out;
  return Object.assign({},out,{_wf:Object.assign({},out._wf,{reimbursements:mergeReimbList(ra,rs)})});
}
// row = loanToDb(local), base = loanToDb(baseline loan) or null, srv = server row.
function mergeLoanRow(row,base,srv){
  var out={};
  Object.keys(row).forEach(function(c){
    var a=row[c],b=base?base[c]:undefined,s=srv[c];
    if(c==="id"||!(c in srv)){out[c]=a;return;}
    if(c==="statement_data"||c==="counseling"){out[c]=(s!==undefined&&s!==null)?s:a;return;}
    if(c==="portal_logins"){out[c]=Array.isArray(s)?s:a;return;}
    if(c==="activity_log"){out[c]=mergeLog(s,a);return;}
    if(c==="uploads"){out[c]=merge3Set(a,base?b:null,s,uploadKey);return;}
    if(c==="tasks"){out[c]=merge3Set(a,base?b:null,s,taskKey);return;}
    if(c==="docs"){
      var d;
      if(base){d=merge3(a||{},b||{},s||{},1,null);if(!isPlainObj(d))d=Object.assign({},a||{});}
      else d=Object.assign({},isPlainObj(s)?s:{},a||{});
      // The admin UI never writes a testimonial — the borrower's always wins.
      if(isPlainObj(s)&&s.testimonial)d=Object.assign({},d,{testimonial:s.testimonial});
      out[c]=d;return;
    }
    if(!base){out[c]=c==="follow_ups"?mergeReimbWf(a,a,s):a;return;}   // no baseline → this tab's copy (old behaviour; reimbursements by id)
    // A row still stored under a pre-10/1 name ("Inquiry") is read as the
    // stage it became, as loanFromDb / loanToDb read it. Otherwise an edit to
    // any OTHER field sees the stage as untouched here, takes the server's
    // value and writes the old name straight back.
    if(c==="stage"){var sv=merge3(a,b,normalizeStage(s),0,null);out[c]=(sv===undefined)?a:sv;return;}
    if(c==="follow_ups"){var f=merge3(a||{},b||{},s||{},2,merge3List);out[c]=mergeReimbWf(isPlainObj(f)?mergeStampedWf(f,a,b,s):a,a,s);return;}
    var v=merge3(a,b,s,0,null);out[c]=(v===undefined)?a:v;
  });
  return out;
}
var SERVER_WRITTEN_LOAN_COLS=["statement_data","uploads","counseling","tasks","activity_log","portal_logins"];
// One loans write. Before supabase/2026-10-02-max-own-pipeline.sql adds the
// loans.owner column, PostgREST refuses any payload naming it (PGRST204 /
// 42703): the write goes once more without it and the column is remembered
// as missing for the session (lib.js), so later saves leave it off. The
// owner still rides in follow_ups._wf, so nothing is lost meanwhile.
async function loansWrite(rows,run){
  var r=await run(rows);
  if(r&&r.error&&isLoanOwnerColumnError(r.error)&&rows.some(function(x){return x&&Object.prototype.hasOwnProperty.call(x,"owner");})){
    markLoansOwnerColumnMissing();
    console.warn("[LoanFlow save] loans.owner column is missing in the database — saving without it (the owner is still kept on the file). Expected while Max's own pipeline is dormant — supabase/2026-10-02-max-own-pipeline.sql adds it, run only when that is switched back on.");
    r=await run(stripLoanOwner(rows));
  }
  return r;
}
// What a brand-new file looked like when this tab tried to insert it — tells
// "our own earlier attempt already landed" apart from "someone else already
// has this id" (see saveLoansMerged).
function loanFp(r){var wf=(r&&r.follow_ups&&r.follow_ups._wf)||{};return[r.id,r.borrower_name||"",String(r.phone||"").replace(/\D/g,""),wf.sourceLeadId||"",wf.owner||""].join("|");}
// Upsert only the given (admin-edited) loans, each merged with the current
// server row. `baseById` is the save baseline (Map id → loan). On a failed
// server read (tried twice) we DROP the server-written columns from the
// payload entirely — PostgREST leaves omitted columns untouched on update, so
// the save still persists admin edits without any risk of blanking a borrower
// submission. follow_ups is ONE jsonb column holding the whole _wf (interest
// level, Booked, board comment, follow-up date, scenario history…): it is
// dropped too when no row changed it, and when a row DID change it the save
// fails instead (the autosave retries) — written blind from this tab's copy it
// would replace the server's _wf and undo whatever another device set since.
//
// A file that is NEW here (not in the baseline) is never written over a row
// the server already has under that id: ids are minted locally, so a second
// device (or the other user, before per-staff series) can mint the same one.
// New files insert with on-conflict-do-nothing; one that didn't land — or
// whose id is already on the server — is either our own earlier attempt (its
// fingerprint matches one we sent: merge as usual) or someone else's file
// (returned in _collided, so the caller renumbers ours and never touches theirs).
async function saveLoansMerged(changed,baseById,fpMap){
  fpMap=fpMap||Object.create(null);
  var baseOf=function(id){return baseById&&baseById.get?baseById.get(id):null;};
  var baseRow=function(id){var bl=baseOf(id);return bl?loanToDb(bl):null;};
  var rows=changed.map(loanToDb);
  var ids=rows.map(function(r){return r.id;}).filter(Boolean);
  var serverById=Object.create(null);
  var readOk=false;
  // A transient blip (network, PostgREST 5xx / timeout) gets one more try
  // before the save settles for the safe-omit path below.
  for(var tries=0;ids.length&&!readOk&&tries<2;tries++){
    try{
      var res=await supabase.from('loans').select('*').in('id',ids);
      if(!res.error&&res.data){readOk=true;res.data.forEach(function(r){serverById[r.id]=r;});}
    }catch(e){/* readOk stays false → safe-omit path below */}
  }
  var collided=[];
  var isMine=function(id,srv){return(fpMap[id]||[]).indexOf(loanFp(srv))>=0;};
  var fresh=[],known=[];
  rows.forEach(function(row){(baseOf(row.id)?known:fresh).push(row);});
  // Server copy unreadable and this tab changed follow_ups on a file it
  // already knew → write nothing; the autosave retries (scheduleRetry) and the
  // next save merges it (mergeLoanRow / mergeStampedWf) once the read works.
  var fuUntouched=known.every(function(row){var br=baseRow(row.id);return!!br&&seq(row.follow_ups,br.follow_ups);});
  if(!readOk&&!fuUntouched){
    console.warn("[LoanFlow save] couldn't read the server copy of the changed files — not writing their follow-ups blind (that would undo another device's interest level / Booked / note); will retry");
    return{error:{message:"couldn't read the server copy of the files before saving (follow-ups not written blind) — will retry",code:"LF_LOANS_READ_FAILED"}};
  }
  // ── New files ──
  var toInsert=[],freshMerge=[];
  fresh.forEach(function(row){
    var srv=readOk?serverById[row.id]:null;
    if(srv){if(isMine(row.id,srv))freshMerge.push(mergeLoanRow(row,null,srv));else collided.push(row.id);return;}
    toInsert.push(row);
  });
  if(toInsert.length){
    toInsert.forEach(function(r){(fpMap[r.id]=fpMap[r.id]||[]).push(loanFp(r));});
    var ins=await loansWrite(toInsert,function(rs){return supabase.from('loans').upsert(rs,{onConflict:'id',ignoreDuplicates:true}).select('id');});
    if(ins.error)return{error:ins.error};
    var got=new Set((Array.isArray(ins.data)?ins.data:[]).map(function(x){return x.id;}));
    var missed=toInsert.filter(function(r){return!got.has(r.id);});
    if(missed.length){
      var q=await supabase.from('loans').select('*').in('id',missed.map(function(r){return r.id;}));
      if(q.error)return{error:q.error};
      var srvM=Object.create(null);(q.data||[]).forEach(function(x){srvM[x.id]=x;});
      missed.forEach(function(row){var s=srvM[row.id];if(s&&isMine(row.id,s))freshMerge.push(mergeLoanRow(row,null,s));else collided.push(row.id);});
    }
  }
  // ── Files this tab already knew ──
  var upRows;
  if(!readOk){
    // Couldn't confirm server state — don't touch the server-written columns.
    // docs is dropped too when no row changed it (keeps a testimonial safe),
    // and so is follow_ups (no row changed it, or we returned above) — the
    // other device's _wf (interest / Booked / note…) stays as it is.
    // Every row omits the same keys, so the batch stays uniform (PostgREST
    // fills a key missing from one row with NULL).
    var docsUntouched=known.every(function(row){var br=baseRow(row.id);return!!br&&seq(row.docs,br.docs);});
    upRows=known.map(function(row){
      SERVER_WRITTEN_LOAN_COLS.forEach(function(c){delete row[c];});
      if(docsUntouched)delete row.docs;
      delete row.follow_ups;
      return row;
    });
  }else{
    upRows=known.map(function(row){
      var srv=serverById[row.id];
      if(!srv)return row; // deleted elsewhere meanwhile — write it back rather than lose the edit
      return mergeLoanRow(row,baseRow(row.id),srv);
    });
  }
  var out={data:null,error:null};
  var upsertLoans=function(rs){return supabase.from('loans').upsert(rs);};
  if(upRows.length){var w=await loansWrite(upRows,upsertLoans);if(w&&w.error)return w;out=w||out;}
  if(freshMerge.length){var w2=await loansWrite(freshMerge,upsertLoans);if(w2&&w2.error)return w2;}
  if(collided.length)console.warn("[LoanFlow save] loan id(s) already used by another file on the server — not overwritten: "+collided.join(", "));
  return Object.assign({},out,{_collided:collided});
}

// ═══════ MARKETING LEADS — merge on write ═══════
// Reece and Max work the same leads. Writing this tab's whole row from its
// (possibly hours-old) copy reverted the other user's note, do-not-contact
// flag, follow-up date and owner tag. So, like loans, a changed lead is
// merged with a fresh server read first:
//   • tags — set merge against the baseline (one owner tag survives: the
//     server's when both sides stamped one).
//   • mailings — merged per mailing (by id): an edit on either side is kept.
//   • history — append-style set merge; the {type:"profile"} entry is merged
//     key by key (quoteLog as a list — scenario history is never lost).
//   • every other column — three-way: what this tab changed wins, the rest
//     takes the server's value. do_not_contact is never cleared by a row with
//     no baseline.
function mailKey(m){return(isPlainObj(m)&&m.id)?"i:"+m.id:"j:"+stableStr(m);}
function merge3Keyed(a,b,s,keyFn){
  a=Array.isArray(a)?a:[];s=Array.isArray(s)?s:[];
  var hasBase=Array.isArray(b);
  var bm=new Map(),sm=new Map(),am=new Set();
  (hasBase?b:[]).forEach(function(x){bm.set(keyFn(x),x);});
  s.forEach(function(x){sm.set(keyFn(x),x);});
  a.forEach(function(x){am.add(keyFn(x));});
  var out=[];var seen=new Set();
  a.forEach(function(x){
    var k=keyFn(x);
    if(hasBase&&bm.has(k)&&!sm.has(k))return;
    seen.add(k);
    if(bm.has(k)&&sm.has(k)){var v=merge3(x,bm.get(k),sm.get(k),1,null);out.push(v===undefined?x:v);}else out.push(x);
  });
  s.forEach(function(x){var k=keyFn(x);if(seen.has(k)||am.has(k)||(hasBase&&bm.has(k)))return;seen.add(k);out.push(x);});
  return out;
}
function isProfileEntry(h){return isPlainObj(h)&&h.type==="profile";}
function lastProfile(hist){var a=Array.isArray(hist)?hist:[];for(var i=a.length-1;i>=0;i--){if(isProfileEntry(a[i]))return a[i];}return null;}
function mergeLeadHistory(a,b,s){
  var strip=function(h){return(Array.isArray(h)?h:[]).filter(function(x){return!isProfileEntry(x);});};
  var list=merge3List(strip(a),Array.isArray(b)?strip(b):null,strip(s));
  var pa=lastProfile(a),pb=Array.isArray(b)?lastProfile(b):null,ps=lastProfile(s);
  if(!pa&&!ps)return list;
  var da=(pa&&isPlainObj(pa.data))?pa.data:{},db=(pb&&isPlainObj(pb.data))?pb.data:{},ds=(ps&&isPlainObj(ps.data))?ps.data:{};
  var md=merge3(da,db,ds,1,merge3List);
  if(!isPlainObj(md))md=da;
  var prof=Object.assign({},pa||ps,{data:md});
  return list.concat([prof]);
}
function ownerTagsOf(tags){return(Array.isArray(tags)?tags:[]).filter(function(t){return /^owner:/.test(String(t||""));});}
function mergeLeadRow(row,base,srv){
  var out={};
  Object.keys(row).forEach(function(c){
    var a=row[c],b=base?base[c]:undefined,s=srv[c];
    if(c==="id"||c==="updated_at"||!(c in srv)){out[c]=a;return;}
    if(c==="tags"){
      var t=merge3Set(a,base?b:null,s,function(x){return String(x);});
      var own=ownerTagsOf(t);
      if(own.length>1){
        // Both sides claimed it — the first claim (already on the server) stands.
        var keep=ownerTagsOf(s)[0]||ownerTagsOf(a)[0];
        var once=false;
        t=t.filter(function(x){if(!/^owner:/.test(String(x||"")))return true;if(x!==keep||once)return false;once=true;return true;});
      }
      out[c]=t;return;
    }
    if(c==="mailings"){out[c]=merge3Keyed(a,base?b:null,s,mailKey);return;}
    if(c==="history"){out[c]=mergeLeadHistory(a,base?b:null,s);return;}
    if(!base){out[c]=(c==="do_not_contact")?(!!a||!!s):a;return;}
    var v=merge3(a,b,s,0,null);out[c]=(v===undefined)?a:v;
  });
  return out;
}
// Upsert the changed leads, each merged with a fresh server read. A failed
// read fails the save (the retry runs) — an unmerged write is never sent.
async function saveLeadsMerged(changed,baseById){
  var rows=changed.map(leadToDb);
  var ids=rows.map(function(r){return r.id;});
  var serverById=Object.create(null);
  var READ=150,PAR=4;
  var chunks=[];for(var i=0;i<ids.length;i+=READ)chunks.push(ids.slice(i,i+READ));
  for(var c=0;c<chunks.length;c+=PAR){
    var batch=chunks.slice(c,c+PAR).map(function(ch){return supabase.from('marketing_leads').select('*').in('id',ch);});
    var got=await Promise.all(batch);
    for(var g=0;g<got.length;g++){
      if(!got[g]||got[g].error)return{error:(got[g]&&got[g].error)||{message:"lead read failed"}};
      (got[g].data||[]).forEach(function(r){serverById[r.id]=r;});
    }
  }
  var out=rows.map(function(row){
    var srv=serverById[row.id];
    if(!srv)return row;
    var bl=baseById&&baseById.get?baseById.get(row.id):null;
    return mergeLeadRow(row,bl?leadToDb(bl):null,srv);
  });
  var CHUNK=1000;var last={data:null,error:null};
  for(var j=0;j<out.length;j+=CHUNK){
    var w=await supabase.from('marketing_leads').upsert(out.slice(j,j+CHUNK));
    if(w&&w.error)return w;
    last=w||last;
  }
  return last;
}
// Rows of a table whose in-memory object differs from the save baseline
// (new or edited) — never the whole array, so a stale tab can't re-send rows
// it didn't touch (a revoked portal code, another user's project edits).
function changedRowsVs(arr,baseArr){
  var bm=new Map();(Array.isArray(baseArr)?baseArr:[]).forEach(function(r){if(r&&r.id)bm.set(r.id,r);});
  var seen=Object.create(null);var out=[];
  (Array.isArray(arr)?arr:[]).forEach(function(r){if(!r||!r.id||seen[r.id])return;seen[r.id]=1;if(bm.get(r.id)!==r)out.push(r);});
  return out;
}
// Cheap per-row change signature for the 30s poll. Includes the row content,
// not just updated_at, so a borrower/Max write is noticed even before the
// loans_touch_updated_at trigger (supabase/2026-10-01-two-users.sql) is run.
function strHash(str){var h=5381;for(var i=0;i<str.length;i++)h=((h<<5)+h+str.charCodeAt(i))|0;return h;}
function rowSig(r){var o=Object.assign({},r);delete o.updated_at;return(r.updated_at||"")+"#"+strHash(JSON.stringify(o));}

// ═══════ SETTINGS — merge on write ═══════
// The settings row is one blob (rate sheets, swap history, DM tags, RC…).
// Writing a stale tab's whole copy wiped newer sheets / swap marks loaded
// elsewhere (SQL sheet loads, the other user, a second device). Now: re-read
// the row, and keep only what THIS tab changed — per top-level setting, per
// rate sheet, and per field inside a sheet. Swap marks merge as a set.
function mergeRateSheets3(a,b,s){
  a=isPlainObj(a)?a:{};b=isPlainObj(b)?b:{};s=isPlainObj(s)?s:{};
  var out={};
  keysOf3(a,b,s).forEach(function(k){
    var v;
    if(k==="_swapHistory"){
      v=merge3List(Array.isArray(a[k])?a[k]:[],Array.isArray(b[k])?b[k]:[],Array.isArray(s[k])?s[k]:[]);
      if(v.length>SWAP_HISTORY_MAX)v=v.slice(-SWAP_HISTORY_MAX);
    } else v=merge3(a[k],b[k],s[k],1,merge3List);
    if(v!==undefined)out[k]=v;
  });
  return out;
}
function mergeSettings3(local,base,server){
  if(!server)return local;
  if(!base)return Object.assign({},server,local);
  var out={};
  keysOf3(local,base,server).forEach(function(k){
    var v=k==="rateSheets"?mergeRateSheets3(local[k],base[k],server[k]):merge3(local[k],base[k],server[k],0,null);
    if(v===undefined)v=(local[k]!==undefined)?local[k]:server[k];
    if(v!==undefined)out[k]=v;
  });
  return out;
}

// ═══════ TIERED COLUMN FALLBACK ═══════
// When a migration hasn't been run, PostgREST rejects the whole upsert for one
// unknown column (PGRST204). Strip ONLY the named column and retry — the old
// fallback dropped straight to a 7-column slim payload, silently losing vendor
// / recurring / receipt path on every expense save while the pill said
// "All changes saved". Missing columns are remembered for the session (no
// failed first attempt on every save) and reported so the pill can warn.
var MISSING_COLS=Object.create(null);   // table → {column:true}
function missingColumnFrom(err){
  var msg=(err&&err.message)||"";
  var m=/Could not find the '([^']+)' column/i.exec(msg)||/column "([^"]+)" of relation/i.exec(msg)||/column [a-z_]+\.([a-z_]+) does not exist/i.exec(msg);
  return m?m[1]:null;
}
function stripMissing(table,rows){
  var miss=MISSING_COLS[table];var ks=miss?Object.keys(miss):[];
  if(!ks.length)return rows;
  return rows.map(function(r){var o=Object.assign({},r);ks.forEach(function(k){delete o[k];});return o;});
}
// run(rows) → Promise<{data,error}>. Resolves with `_missing` = ["table.col"]
// when columns were stripped, `_slim` when it had to fall all the way back.
async function writeTiered(table,rows,run,slimRows){
  var cur=stripMissing(table,rows);var r=null;
  for(var i=0;i<8;i++){
    r=await run(cur);
    var code=r&&r.error&&r.error.code;
    if(!r||!r.error||(code!=="PGRST204"&&code!=="42703"))break;
    var col=missingColumnFrom(r.error);
    if(!col||!cur.length||!(col in cur[0])){
      if(slimRows){
        console.warn("[LoanFlow save] "+table+": unknown column error ("+(r.error.message||code)+") — falling back to the slim payload. Run supabase/2026-10-01-two-users.sql.");
        r=await run(slimRows);r=Object.assign({},r,{_slim:true,_missing:[table+" (extra fields)"]});
      }
      return r;
    }
    (MISSING_COLS[table]=MISSING_COLS[table]||{})[col]=true;
    console.warn("[LoanFlow save] "+table+"."+col+" column is missing in the database — saving without it. Run supabase/2026-10-01-two-users.sql, then reload.");
    cur=stripMissing(table,cur);
  }
  var miss=MISSING_COLS[table]?Object.keys(MISSING_COLS[table]):[];
  if(r&&!r.error&&miss.length&&rows.length&&miss.some(function(c){return c in rows[0];}))
    r=Object.assign({},r,{_missing:miss.filter(function(c){return c in rows[0];}).map(function(c){return table+"."+c;})});
  return r;
}

// Advance a per-row baseline (loans / leads / expenses) for exactly the rows a
// save sent, keeping every other entry — including ones another code path
// (lead download merge, adoptLeads, the poll) put there mid-save.
function patchBaseline(baseArr,sentRows){
  var base=Array.isArray(baseArr)?baseArr:[];
  if(!sentRows||!sentRows.length)return base;
  var byId=new Map();sentRows.forEach(function(r){if(r&&r.id)byId.set(r.id,r);});
  var seen=new Set();
  var out=base.map(function(r){if(r&&r.id&&byId.has(r.id)){seen.add(r.id);return byId.get(r.id);}return r;});
  byId.forEach(function(r,id){if(!seen.has(id))out.push(r);});
  return out;
}

// ═══════ EXPENSES — insert-only for new rows ═══════
// Expense ids are minted locally (max+1), so a stale tab or second device can
// mint an id that already exists on the server. New rows therefore INSERT
// with on-conflict-do-nothing: an existing row is never overwritten. A row
// that didn't land is either our own earlier attempt (its fingerprint matches
// one we sent — then the current version is upserted over it) or someone
// else's (returned in _collided so the caller renumbers ours).
function expFp(r){return[r.id,r.date||"",r.category||"",r.description||"",Number(r.amount)||0].join("|");}
async function insertNewExpenses(newExp,fpMap){
  var payload=newExp.map(expenseToDb);
  payload.forEach(function(p){(fpMap[p.id]=fpMap[p.id]||[]).push(expFp(p));});
  var r=await writeTiered('expenses',payload,function(rows){return supabase.from('expenses').upsert(rows,{onConflict:'id',ignoreDuplicates:true}).select('id');},newExp.map(expenseToDbSlim));
  if(!r||r.error)return r;
  var got=new Set((Array.isArray(r.data)?r.data:[]).map(function(x){return x.id;}));
  var missingIds=payload.filter(function(p){return!got.has(p.id);}).map(function(p){return p.id;});
  if(!missingIds.length)return r;
  var q=await supabase.from('expenses').select('id,date,category,description,amount').in('id',missingIds);
  if(q.error)return{error:q.error};
  var srv=Object.create(null);(q.data||[]).forEach(function(x){srv[x.id]=x;});
  var mine=[],foreign=[];
  missingIds.forEach(function(id){var s=srv[id];if(s&&(fpMap[id]||[]).indexOf(expFp(s))>=0)mine.push(id);else foreign.push(id);});
  if(mine.length){
    var mineRows=newExp.filter(function(e){return mine.indexOf(e.id)>=0;});
    var u=await writeTiered('expenses',mineRows.map(expenseToDb),function(rows){return supabase.from('expenses').upsert(rows);},mineRows.map(expenseToDbSlim));
    if(u&&u.error)return u;
  }
  return Object.assign({},r,{_collided:foreign});
}

// ═══════ RATE SHEET DEFAULTS ═══════
const RS_DEFAULTS={
  foaHecmAdj:{label:"FOA HECM Monthly Adj (5% Cap)",margins:"3.000:1500/2.875:1750/2.750:2000/2.625:2500/2.500:3000/2.375:3500/2.250:4000/2.125:4500/2.000:5000/1.875:5500/1.750:6000/1.625:6500/1.500:7000/1.375:7500/1.250:8000",index:"3.470",swapRate:"5.500",note:"1yr CMT + margin | 10-day lock | Lender-paid per margin",updated:"02/10/2026"},
  foaHecmFixed:{label:"FOA HECM Fixed",tiers:"7.930:2000/7.810:3000/7.680:4250/7.560:6000",note:"Lender-paid comp per tier + origination separate | Premium 103 | Age 62+ excl NC",updated:"02/10/2026"},
  foaHSStd:{label:"FOA HomeSafe Standard (Fixed)",tiers:"7.990:1.0/8.950:0.0/8.980:2.0",minComp:"5000",prem:"103",note:"Orig % of PL payable to FOA | $200k-$4M | 55+ (60 MA/NY/WA, 62 NC/TX)",updated:"02/10/2026"},
  foaHSIntro:{label:"FOA HomeSafe Intro (Fixed)",tiers:"8.990:4.0",minComp:"5000",prem:"103",note:"4% orig to FOA | First-time reverse only | Excl MN,MO,NY,UT",updated:"02/10/2026"},
  // Original HomeSafe Select (foaHSSelect) is retired — no new apps since 6/5,
  // legacy closings end 10/5/26. Calc no longer prices it, so no default card.
  foaHSSelIntro:{label:"FOA HomeSafe Select Intro (ARM)",margins:"5.750/5.625",origPct:"1.5",origCap:"20000",note:"1.5% of MCA cap $20k to broker | First-time only",updated:"02/10/2026"},
  foaHS2:{label:"FOA HomeSafe Second",tiers:"8.990/9.490",flatOrig:"3995",minComp:"5000",prem:"103",note:"Flat $3,995 orig to FOA | $50k-$1M | 2nd lien",updated:"02/10/2026"},
  mooSEFixedPlus:{label:"MoO SecureEquity Fixed Plus",tiers:"8.990",origPct:"4.0",origCap:"999999",prem:"103",note:"4% orig to MoO NO MAX | $200k min PL | LESA 9.240",updated:"02/17/2026"},
  mooSEFixed:{label:"MoO SecureEquity Fixed",tiers:"8.740/8.950",origPct:"1.0",origCap:"30000",prem:"103",note:"1% orig cap $30k to MoO | Tier 1 lower rate/LTV | $200k min PL",updated:"02/17/2026"},
  mooSEARM:{label:"MoO SecureEquity ARM",margins:"6.250/6.125/6.000/5.875/5.749",origPct:"broker",origCap:"30000",note:"Orig to broker cap $30k | Floor = max(start-1.5%, 5.5%) | Premium on UPB",updated:"02/17/2026"},
  mooHecmAdj:{label:"MoO HECM Monthly Adj (5% Cap)",margins:"3.000:1500/2.875:1750/2.750:2000/2.625:2500/2.500:3000/2.375:3500/2.250:4000/2.125:4500/2.000:5000/1.875:5500/1.750:6000/1.625:6500/1.500:7000/1.375:7500/1.250:8000",index:"3.470",note:"1yr CMT + margin | Similar structure to FOA",updated:"02/17/2026"},
  smartHecmAdj:{label:"SmartFi HECM Monthly Adj (5% Cap)",margins:"3.000:111.875/2.875:111.875/2.750:112.125/2.625:111.625/2.500:111.125/2.375:110.775/2.250:109.250/2.125:106.775/2.000:106.180/1.875:104.705/1.750:102.480/1.625:101.425/1.500:98.505/1.375:96.805/1.250:95.375",index:"3.680",swapRate:"4.300",note:"1yr CMT + margin | Premium % from PLU 0-10 column (edit if utilization differs)",updated:"04/28/2026"},
  smartHecmAdjAnnual:{label:"SmartFi HECM Annual Adj (5% Cap)",margins:"3.500:100.000",index:"3.680",note:"Annual reset | Single margin tier | All PLU bands flat at 100",updated:"04/28/2026"},
  smartHecmFixed:{label:"SmartFi HECM Fixed",tiers:"7.930",prem:"103.5",note:"Full HECM origination fee | Premium 103.5 | Lender credit 0",updated:"04/28/2026"},
  // svcTiers / svcMargins are SmartFi's "Choice Service" pricing — active
  // service members & veterans only (same price/PLF, lower rates). The calc
  // uses them ONLY when the veteran box is checked; never merge them into
  // the regular tiers or every non-veteran quote goes out at rates SmartFi
  // won't honor.
  smartChoiceFixedSaver:{label:"SmartFi Choice Fixed Saver",tiers:"7.890",svcTiers:"7.390",origPct:"1.0",prem:"103.5",note:"Single disbursement only | 1% origination | Approved: AL,AZ,CA,CO,DC,FL,GA,ID,IL,IN,LA,MI,MT,NJ,NM,OH,OK,OR,PA,SC,TX,UT,WA",updated:"04/28/2026"},
  smartChoiceFixedMax:{label:"SmartFi Choice Fixed Max",tiers:"8.980",svcTiers:"8.480",origPct:"4.0",prem:"103.5",note:"4% orig (PL-based, paid to lender) + 1% additional for PL<$200k",updated:"04/28/2026"},
  smartChoiceFixedPro:{label:"SmartFi Choice Fixed Pro",tiers:"8.990",svcTiers:"8.490",origPct:"1.0",prem:"103.5",note:"1% orig | Borrower-paid comp = price 100.00 (not avail on Max PLF)",updated:"04/28/2026"},
  smartChoiceFixedCore:{label:"SmartFi Choice Fixed Core",tiers:"9.125",svcTiers:"8.625",origPct:"2.0",prem:"103.5",note:"2% orig | 12mo seasoning required for prop-to-prop refi",updated:"04/28/2026"},
  smartChoiceFlex:{label:"SmartFi Choice Flex (ARM/LOC)",margins:"6.125:105.125/5.990:105.000/5.875:104.250/5.750:103.500/5.625:102.750",svcMargins:"5.625:105.125/5.490:105.000/5.375:104.250/5.250:103.500/5.125:102.750/4.990:102.250/4.875:101.750",note:"Line of credit only | 25% min initial draw | 5% caps + floor max(start-1.5%, 5.5%) | Approved: AZ,CA,CO,FL,GA,MI,PA,TX,UT",updated:"04/28/2026"},
  lbHecmAdj:{label:"Longbridge HECM Monthly Adj (5% Cap)",margins:"3.000:111.875/2.875:111.875/2.750:112.125/2.625:111.625/2.500:111.125/2.375:110.775/2.250:109.250/2.125:106.775/2.000:106.180/1.875:104.705/1.750:102.480/1.625:101.425/1.500:98.505/1.375:96.805/1.250:95.375",index:"3.470",swapRate:"3.470",note:"DEFAULT ESTIMATES (premium-priced) — update with current Longbridge rate sheet",updated:"default"},
  lbHecmAdjAnnual:{label:"Longbridge HECM Annual Adj (5% Cap)",margins:"",index:"",note:"Upload latest Longbridge rate sheet to populate",updated:"—"},
  lbHecmFixed:{label:"Longbridge HECM Fixed",tiers:"7.930:2000/7.810:3000/7.680:4250/7.560:6000",note:"DEFAULT ESTIMATES — update with current Longbridge rate sheet",updated:"default"},
  lbPlatinumFixed:{label:"Longbridge Platinum Fixed",tiers:"8.750:1.0/8.990:0.0",prem:"103",note:"Proprietary jumbo | DEFAULT ESTIMATES — update with current Longbridge sheet",updated:"default"},
  lbPlatinumLOC:{label:"Longbridge Platinum LOC (ARM)",margins:"5.750/5.625",note:"Proprietary jumbo LOC | DEFAULT ESTIMATES — update with current Longbridge sheet",updated:"default"},
};

// Merge a saved rate-sheet object over the defaults. A lender entry that is
// MISSING from the saved sheets falls back to the built-in default, so all four
// lenders (FOA, MoO, SmartFi, Longbridge) generate programs even if an older
// saved sheet lacked them. An entry that is saved but deliberately EMPTIED
// (tiers/margins "" — or disabled:true) stays off: clearing a sheet retires
// that program instead of quietly bringing back February's default pricing.
// Labels and notes are backfilled from the defaults WITHOUT touching any
// pricing field, so every card in the rate-sheet editor has its title.
const rsHasPricing=(e)=>!!e&&!!((e.tiers&&String(e.tiers).length)||(e.margins&&String(e.margins).length));
const rsExplicitlyOff=(e)=>!!e&&(e.disabled===true||((("tiers" in e)||("margins" in e))&&!rsHasPricing(e)));
const mergeRs=(saved)=>{
  const out=Object.assign({},RS_DEFAULTS,saved||{});
  Object.keys(RS_DEFAULTS).forEach((k)=>{
    const d=RS_DEFAULTS[k];const cur=out[k];
    if(cur===d)return;
    if(!cur||typeof cur!=="object"){out[k]=d;return;}
    const off=rsExplicitlyOff(cur);
    // A stub with no pricing fields at all (e.g. only a swapRate) → defaults
    // under whatever it does carry.
    if(!off&&!rsHasPricing(cur)&&rsHasPricing(d)){out[k]=Object.assign({},d,cur);return;}
    const patch={};
    if(!cur.label&&d.label)patch.label=d.label;
    if(cur.note===undefined&&d.note)patch.note=d.note;
    // Saved sheets from before the Choice Service split won't carry the
    // veterans-only fields — backfill them (only when absent, never over a
    // deliberate "") without touching the saved regular tiers.
    if(!off)["svcTiers","svcMargins"].forEach((f)=>{if(d[f]&&cur[f]===undefined)patch[f]=d[f];});
    if(Object.keys(patch).length)out[k]=Object.assign({},cur,patch);
  });
  return out;
};


// ═══════ LIMITED LOGIN (Max) ═══════
// No leads, no files and nowhere to write them — these stay the same object
// every render so nothing re-renders for them. (Max's own files — myLoans /
// setMyLoans in App — are dormant since 10/2: his access has no pipeline.)
const NO_ROWS=Object.freeze([]);
function noop(){}
// One extra line under the Practice banner, per screen. The calculator line
// only mentions a Pitch for a login that has a pipeline to pitch into.
const PRACTICE_PIPELINE_DETAIL="New leads, notes, stage moves, tasks and pitches stay on this screen for this session only — nothing is saved and no document is uploaded. They're gone when Practice mode is turned off or you sign out.";
const PRACTICE_CALC_DETAIL="Calculator runs aren't logged anywhere while Practice mode is on. Turn it off in Settings.";
const PRACTICE_DETAIL={
  calculator:"Calculator runs aren't logged anywhere and a Pitch doesn't reach your pipeline while Practice mode is on. Turn it off in Settings.",
  pipeline:PRACTICE_PIPELINE_DETAIL,
  loanDetail:PRACTICE_PIPELINE_DETAIL,
  inquiry:PRACTICE_PIPELINE_DETAIL,
  expenses:"Expense changes stay on this screen for this session only — no receipt uploads, nothing saved. They're gone when Practice mode is turned off or you sign out.",
  library:"Questions and answers here aren't kept — the chat is gone when Practice mode is turned off or you sign out.",
  settings:"Turn the switch below off to go back to saving normally.",
};

// ═══════ MAIN APP ═══════
export default function App(){
  // ── Load persisted state or use defaults ──
  const saved=loadState();
  const [user,setUser]=useState(null);
  const [adminPw,setAdminPw]=useState("");
  const [adminErr,setAdminErr]=useState(false);
  const [borrowerPw,setBorrowerPw]=useState("");
  const [borrowerErr,setBorrowerErr]=useState(false);

  // Admin gate — hides the admin login form unless the URL path matches
  // ADMIN_PATH or MAX_PATH (or the gate was already unlocked in this tab
  // session). See the constant definitions in theme.js for details.
  const [adminGateOpen,sAdminGateOpen]=useState(()=>{
    if(typeof window!=="undefined"&&(window.location.pathname===ADMIN_PATH||window.location.pathname===MAX_PATH))return true;
    try{return typeof sessionStorage!=="undefined"&&sessionStorage.getItem("loanflow_admin_gate")==="1";}
    catch(e){return false;}
  });
  // Which staff login the admin form signs in as: /max → Max, /crmadmin →
  // Reece. Remembered for the tab so a reload on "/" keeps the same form.
  const [loginAs,setLoginAs]=useState(()=>{
    try{
      if(typeof window!=="undefined"&&window.location.pathname===MAX_PATH)return"max";
      if(typeof window!=="undefined"&&window.location.pathname===ADMIN_PATH)return"reece";
      return(typeof sessionStorage!=="undefined"&&sessionStorage.getItem("loanflow_login_as")==="max")?"max":"reece";
    }catch(e){return"reece";}
  });
  useEffect(()=>{
    const p=window.location.pathname;
    if(p===ADMIN_PATH||p===MAX_PATH){
      sAdminGateOpen(true);
      const who=p===MAX_PATH?"max":"reece";setLoginAs(who);
      try{sessionStorage.setItem("loanflow_admin_gate","1");sessionStorage.setItem("loanflow_login_as",who);}catch(e){}
    }
  },[]);
  // Signed-in staff member: "reece" | "max" (null when logged out). Both are
  // user==="admin"; what each may see and do is src/access.js (Max: his own
  // expenses, the calculator, the library — never Reece's leads or files).
  // Views default to "reece" when undefined.
  const [staff,setStaff]=useState(null);
  const staffKey=staff||"reece";
  // What this login can use (src/access.js). Reece: everything. Max:
  // Calculator (standalone, no Pitch), his own Expenses, the Lender Library
  // chatbot (read-only browsing) and a short Settings page. The database
  // enforces the same (supabase/2026-10-01-max-limited-access.sql) — this is
  // the front door.
  const access=staffAccess(staffKey);
  const limited=!access.leads;
  // A login working its own files only (access.ownFilesOnly — Max's own
  // pipeline, DORMANT since 10/2: his access has no pipeline, so this is
  // false for him and every own-files path below stays switched off).
  const ownOnly=!!access.pipeline&&!!access.ownFilesOnly;
  const limitedRef=useRef(limited);limitedRef.current=limited;
  // Practice mode (Max only, on his login): nothing he does is saved anywhere.
  const [staffPrefs]=useStaffPrefs(staffKey);
  const practice=limited&&staffPrefs.practice===true;
  // Bumped to re-run the Supabase load (Practice mode turned off → throw away
  // the session-only edits and re-read what is really saved).
  const [loadNonce,setLoadNonce]=useState(0);
  // True from the moment Practice mode turns off until that re-read lands:
  // the practice edits still in memory must never be mistaken for changes.
  const discardPracticeRef=useRef(false);
  const [nav,setNav]=useState("dashboard");
  const [navOpen,setNavOpen]=useState(false);
  const [autoPanelOpen,setAutoPanelOpen]=useState(false);

  // ── New Call console ──
  // A full-screen overlay rather than a nav destination: a call can start
  // while he is anywhere in the app, and whatever he was doing is still
  // underneath when the call ends.
  const [callOpen,setCallOpen]=useState(false);
  const [callSeed,setCallSeed]=useState(null);   // {phone} or {prospect,callEpoch}
  const [callFlash,setCallFlash]=useState("");
  const startCall=useCallback(function(seed){setCallSeed(seed||null);setCallOpen(true);},[]);
  // The console registers a flush for whatever is typed but not yet on the
  // record (note box, disposition note, card edits). Logout and the idle
  // sign-out run it FIRST and let React commit the resulting setLoans /
  // setMktgLeads, so that text rides in the final push instead of landing
  // after the session (and the right to write) has ended.
  // Every screen with a typed-but-not-yet-written note registers one here
  // (New Call, Loan Detail's notes drawer + fall-out note, the Dashboard quick
  // notes, Outbound's spoke notes). Logout and the idle sign-out first blur
  // whatever field has focus (its blur-save runs), then run every flusher —
  // all while the session can still write.
  const flushersRef=useRef(new Map());
  const regFlush=useCallback(function(key,fn){if(fn)flushersRef.current.set(key,fn);else flushersRef.current.delete(key);},[]);
  const regCallFlush=useCallback(function(fn){regFlush("call",fn);},[regFlush]);
  const flushCallConsole=async function(){
    const pending=[];
    const run=function(fn){const r=fn();if(r&&typeof r.then==="function")pending.push(r);};
    try{flushSync(function(){const a=typeof document!=="undefined"?document.activeElement:null;if(a&&a!==document.body&&typeof a.blur==="function")a.blur();});}catch(e){}
    flushersRef.current.forEach(function(fn){
      try{flushSync(function(){run(fn);});}catch(e){try{run(fn);}catch(e2){console.warn("note flush failed",e2);}}
    });
    await new Promise(function(r){setTimeout(r,0);});
    // Network writes a flusher started (Outbound's results/notes) — wait for
    // them, but never hang the sign-out on a dead connection.
    if(pending.length)await Promise.race([Promise.all(pending.map(function(p){return p.catch(function(){});})),new Promise(function(r){setTimeout(r,8000);})]);
  };
  const endCall=useCallback(function(msg){
    setCallOpen(false);setCallSeed(null);
    if(msg){setCallFlash(msg);setTimeout(function(){setCallFlash("");},6000);}
  },[]);

  // Cmd/Ctrl+K opens the nav drawer; Cmd/Ctrl+Shift+K starts a call;
  // Escape closes any overlay (the console handles its own Escape so a
  // priced call still gets asked how it ended).
  const newCallOkRef=useRef(access.newCall);newCallOkRef.current=access.newCall;
  useEffect(()=>{
    const onKey=(e)=>{
      if((e.metaKey||e.ctrlKey)&&e.shiftKey&&e.key.toLowerCase()==="k"){if(newCallOkRef.current){e.preventDefault();setCallOpen(true);}return;}
      if((e.metaKey||e.ctrlKey)&&e.key.toLowerCase()==="k"){e.preventDefault();setNavOpen(p=>!p);}
      if(e.key==="Escape"){setNavOpen(false);setAutoPanelOpen(false);}
    };
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[]);

  // ── Session timeout (30 min inactivity) ──
  const SESSION_TIMEOUT=30*60*1000;
  const lastActivity=useRef(Date.now());
  const timeoutTimer=useRef(null);
  useEffect(()=>{
    if(!user)return;
    const resetTimer=()=>{
      lastActivity.current=Date.now();
      clearTimeout(timeoutTimer.current);
      timeoutTimer.current=setTimeout(async()=>{
        // On idle timeout, make sure the Supabase session is killed too so
        // the RLS gate drops back to anon — otherwise an unattended tab
        // would still carry a live JWT. Push any unsaved change first (it
        // can only be written while still signed in).
        try{await flushCallConsole();}catch(e){}
        if(isSupabaseReady()){try{await flushPendingSave();}catch(e){}try{await supabase.auth.signOut();}catch(e){}}
        setUser(null);setAdminPw("");setBorrowerPw("");
      },SESSION_TIMEOUT);
    };
    resetTimer();
    const events=["mousedown","keydown","scroll","touchstart"];
    events.forEach(e=>window.addEventListener(e,resetTimer));
    return ()=>{events.forEach(e=>window.removeEventListener(e,resetTimer));clearTimeout(timeoutTimer.current);};
  },[user]);

  // ── Failed login lockout (5 attempts, 5 min lockout) ──
  const MAX_ATTEMPTS=5;
  const LOCKOUT_DURATION=5*60*1000;
  const [loginAttempts,setLoginAttempts]=useState(0);
  const [lockoutUntil,setLockoutUntil]=useState(0);


  // All persisted settings in one object — loaded from localStorage.
  // adminPasswordHash is a legacy local-dev fallback; the real auth gate
  // is Supabase Auth (see adminLogin below). The placeholder is
  // intentionally unguessable so a fresh install can never be logged into
  // without a real password being set in Supabase Auth first.
  const defaultSettings={
    adminPasswordHash:"UNSET-CHANGE-IN-SUPABASE-AUTH",
    adminPhone:saved?.settings?.adminPhone||"",
    loSplit:saved?.settings?.loSplit!==undefined?saved.settings.loSplit:70,
    hecmLimit:saved?.settings?.hecmLimit||1249125,
    dmCampaigns:saved?.settings?.dmCampaigns||[...DM_CAMPAIGNS],
    rateSheets:mergeRs(saved?.settings?.rateSheets),
    rcEnabled:saved?.settings?.rcEnabled||false,
    rcClientId:saved?.settings?.rcClientId||"",
    rcServerUrl:saved?.settings?.rcServerUrl||"",
    rcSmsFrom:saved?.settings?.rcSmsFrom||"",
  };
  const [settings,setSettings]=useState(defaultSettings);
  const updateSettings=(patch)=>setSettings(p=>{const n={...p,...patch};return n;});

  const [loans,setLoans]=useState(saved?.loans||LOANS_INIT);
  const [mktgLeads,setMktgLeads]=useState(saved?.mktgLeads||[]);
  const [campaigns,setCampaigns]=useState(saved?.campaigns||[]);
  const [expenses,setExpenses]=useState(saved?.expenses||[]);
  // Did THIS session read the expenses from the server? "unknown" until the
  // load, "ok" after a read worked, "error" when it failed — `expenses` is
  // then this browser's saved copy (maybe days old), and Expenses holds
  // Import back until a reload reads the real list.
  const [expensesLoad,setExpensesLoad]=useState("unknown");
  const [borrowerAccts,setBorrowerAccts]=useState(saved?.borrowerAccts||[]);
  const [smsLog,setSmsLog]=useState(saved?.smsLog||[]);
  const [abTests,setAbTests]=useState(saved?.abTests||[]);
  const [touchSchedule,setTouchSchedule]=useState(saved?.touchSchedule||[]);
  const [mailTemplates,setMailTemplates]=useState(saved?.mailTemplates||[]);
  const [sel,setSel]=useState(null);
  const [to,setTo]=useState(null);
  // A pitch opens the deal (Reece, 10/10/26: "When you click on pitch, it
  // automatically takes you to that deal"). Handed to the Inquiry calculator
  // (→ Calc onOpenFile) and the New Call console (which first folds the call
  // into the file and closes). Max's calculators never get it. The file may
  // have been minted in this very click — `loans` here can't have it yet —
  // so sel is just its id; Loan Detail reads the live row out of `loans`.
  const openPitchedFile=useCallback(function(id){
    if(!id)return;
    setSel({id:id,_stub:true});setNav("loanDetail");setTo(null);setNavOpen(false);
  },[]);
  // …and as soon as that row is in `loans`, the stub becomes the live row:
  // every older sel consumer (the Automations panel's AutoHub, Inquiry's
  // re-price) was written for a whole loan — {id} alone crashed AutoHub
  // (a.docs.hoa) after "← Pipeline" → Automations.
  useEffect(function(){
    if(!sel||!sel._stub)return;
    var live=loans.find(function(l){return l.id===sel.id;});
    if(live)setSel(live);
  },[sel,loans]);
  // Cross-component pointer the urgent banner can poke to open a specific
  // marketing-lead detail modal. Marketing reads it on mount/change, opens
  // the modal, then clears it.
  const [leadFocus,setLeadFocus]=useState(null);
  // ── Lazy lead loading ──
  // The marketing database (45K+ rows) is NOT loaded at login. Login fetches
  // only head-counts (for the dashboard tiles) and the handful of alert rows
  // the urgent banner needs. The full table downloads once, on demand, the
  // first time the admin opens a view that actually searches leads
  // (Marketing / New Lead / Settings). `leadsReady` flips true when the full
  // set is in memory; until then `mktgLeads` holds only the alert rows.
  const [leadsReady,setLeadsReady]=useState(false);
  const [leadCounts,setLeadCounts]=useState(null); // {total,contactable} from head-count queries
  const [leadLoadProgress,setLeadLoadProgress]=useState(null); // {loaded,total} while the full lead DB downloads
  const leadsLoadingRef=useRef(false);
  // The never-mailed remainder of the table loads only on request (Marketing's
  // "Load never-mailed leads", Settings backup, the bulk mailing import).
  const [allLeadsLoaded,setAllLeadsLoaded]=useState(false);
  const allLeadsPromiseRef=useRef(null);
  // Bumped whenever a session ends (logout, idle timeout, SIGNED_OUT) and on
  // unmount. Long-running work (lead downloads, save retries) captures it at
  // start and drops its result if it changed — a nav click no longer cancels
  // anything; only the session ending does.
  const sessionGenRef=useRef(0);

  // ── Expenses are per person ──
  // Everything else is shared between Reece and Max. In prod RLS returns only
  // the signed-in user's rows; locally (and against any stale cached copy) we
  // filter by owner too, so Max starts with a blank list and never sees
  // Reece's. Edits made through the view land back in the full array with
  // the other person's rows untouched, and new rows are stamped with owner.
  const myExpenses=useMemo(function(){return expenses.filter(function(e){return e&&(e.owner||"reece")===staffKey;});},[expenses,staffKey]);
  const setMyExpenses=useCallback(function(upd){
    setExpenses(function(prev){
      const isMine=function(e){return e&&(e.owner||"reece")===staffKey;};
      const mine=prev.filter(isMine);
      const others=prev.filter(function(e){return!isMine(e);});
      const next=typeof upd==="function"?upd(mine):upd;
      if(!Array.isArray(next))return prev;
      const had=new Set(mine);
      return others.concat(next.map(function(e){return(e&&!had.has(e)&&!e.owner)?Object.assign({},e,{owner:staffKey}):e;}));
    });
  },[staffKey]);

  // ── Max's files are his own (10/2) ──
  // In prod RLS returns only rows whose owner is "max"; locally (and against
  // any stale cached copy) they are filtered here too, so his screens only
  // ever hold his files. Edits made through his screens land back in the
  // full array with anything else untouched, and a new file with no owner is
  // stamped his. Reece's screens get `loans` / `setLoans` unchanged.
  const myLoans=useMemo(function(){return ownOnly?loans.filter(function(l){return l&&ownerOf(l)===staffKey;}):loans;},[loans,ownOnly,staffKey]);
  const setMyLoans=useCallback(function(upd){
    setLoans(function(prev){
      const isMine=function(l){return l&&ownerOf(l)===staffKey;};
      const mine=prev.filter(isMine);
      const others=prev.filter(function(l){return!isMine(l);});
      const next=typeof upd==="function"?upd(mine):upd;
      if(!Array.isArray(next))return prev;
      return others.concat(next.map(function(l){return(l&&!l.owner)?Object.assign({},l,{owner:staffKey}):l;}));
    });
  },[staffKey]);
  const loansForViews=ownOnly?myLoans:loans;
  const setLoansForViews=ownOnly?setMyLoans:setLoans;

  // ── Follow-ups bell (Reece) ──
  // Follow-ups + appointments that are overdue or due today (lib.js
  // followUpAlertCount — the same items the Command Center's Follow-ups panel
  // lists). Clicking it opens the Command Center scrolled to that panel:
  // fuFocus is bumped and the panel scrolls itself into view. It is cleared
  // the moment the view leaves the Command Center (a file opened from it,
  // the drawer), so coming back — Loan Detail's "Command Center" button —
  // opens at the top, not at Follow-ups again. The count (fuDueCount, below
  // sbLoaded) waits for this session's data.
  const [fuFocus,setFuFocus]=useState(0);
  useEffect(function(){if(nav!=="dashboard")setFuFocus(0);},[nav]);
  const todayIso=isoDatePlus(0);

  // ── Lender Library stays mounted once opened ──
  // So an upload, a search or a half-filled form survives working elsewhere.
  // The Ask-the-Library conversation itself lives in a per-person store
  // (src/libraryChat.js) shared by the Library page and the chat bubble: an
  // answer streams there whichever is mounted, and the nav spinner / the
  // "Library answering…" chip read its busy flag.
  const [libOpened,setLibOpened]=useState(false);
  useEffect(function(){if(nav==="library")setLibOpened(true);},[nav]);
  const libraryBusy=useLibraryChat(user==="admin"?staffKey:null,function(s){return s.busy;});
  // ── Deal basics for the chat ──
  // With a loan file open, the chat's composer shows a removable "Using: TX ·
  // 72 · $650K · HECM refi ✕" chip and sends those basics with the question —
  // never the name, phone, email, address or id (dealContextFromLoan keeps
  // state / age / value / balance / loan type / deal type / product / stage
  // only). The New Call console reports its card the same way (CallConsole.jsx,
  // source "call") and wins while it's open.
  // A file is open in Loan Detail, or being re-priced in the calculator (Inquiry with sel).
  // While the console is open the file underneath is covered by it, so it is
  // not reported at all: on the console's Find step (no card yet) the chip
  // must not show — or send — the hidden file's basics with a question about
  // the caller being looked up. It comes back when the call ends (a ✕ on it
  // is remembered by its key).
  const selLoanLive=(nav==="loanDetail"||nav==="inquiry")&&sel?(loans.find(function(l){return l.id===sel.id;})||sel):null;
  const fileDeal=useMemo(function(){return selLoanLive?dealContextFromLoan(selLoanLive):null;},[selLoanLive]);
  useReportDealContext(user==="admin"&&!callOpen&&access.pipeline?fileDeal:null,"file",selLoanLive?"loan:"+selLoanLive.id:"");
  // "Library ↗" in the floating Ask-the-Library chat box (ChatDock). Stable,
  // so the memoized dock doesn't re-render on every App render. Never while a
  // call is open: the console covers the screen, so the click looked like it
  // did nothing — and underneath it had dropped the open file (sel) and moved
  // the page, so the call ended on the Library instead of that file. The dock
  // disables the button then (libraryLocked); the ref guards it here too.
  const callOpenRef=useRef(callOpen);callOpenRef.current=callOpen;
  const openLibraryFromDock=useCallback(function(){if(callOpenRef.current)return;setNav("library");setSel(null);setTo(null);setNavOpen(false);},[]);

  // Everything tied to ONE signed-in session is reset when it ends, so the
  // next login (same tab — Reece or Max) loads cleanly: the working lead set
  // re-downloads, the save baseline is retaken from the fresh load, and no
  // in-flight download or retry from the old session can land in the new one.
  const resetSessionState=function(){
    sessionGenRef.current++;
    leadsLoadingRef.current=false;allLeadsPromiseRef.current=null;
    lastSavedRef.current={initialized:false};
    adminTablesPollRef.current={leadIds:new Set(),loanSigById:new Map(),settingsSig:""};leadPollMarkRef.current="";
    settingsLoadStateRef.current="unknown";
    setExpensesLoad("unknown");expForeignIdsRef.current=new Set();
    pendingSaveRef.current=null;latestRunSaveRef.current=null;saveInflightRef.current=null;
    clearTimeout(saveTimer.current);
    if(isSupabaseReady()){setLeadsReady(false);setAllLeadsLoaded(false);}
    setLeadLoadProgress(null);setLoadedFor(null);
    setStaff(null);setLibOpened(false);discardPracticeRef.current=false;
    // Stop any Ask-the-Library answer and drop the in-memory conversations and
    // deal tag — the next login (Reece or Max) loads only its own thread.
    resetLibraryChats();
    // Scenario/contact history waiting on a new file's row (or a re-send):
    // stop its timer — signed out it can't write — and re-probe next login.
    resetScenarioSession();
    // The loans.owner column may have been added since (SQL run meanwhile).
    resetLoansOwnerColumn();
    // Practice mode / LO split are re-read from the next login.
    resetStaffPrefs();
    // The Whiteboard's in-memory board and its table probe (this device's copy,
    // with any not-yet-saved marks, stays for the next login to upload).
    resetWhiteboard();
    // The call & email log's table probe ("Saved on this device only" latch)
    // and its database-lookup memory start over for the next login.
    resetContactLogSession();
    // Calls to return: the call-log read, Done marks made, lookups, timers.
    resetCallbacksSession();
    setSaveStatus({state:"idle",failedTables:[],at:null});
  };
  useEffect(function(){if(!user)resetSessionState();},[user]);// eslint-disable-line react-hooks/exhaustive-deps
  // ── Whiteboard (10/2) — Reece's misc notes: the bottom-left bubble + the
  // Command Center panel, one store (src/whiteboard.js). Loaded at sign-in,
  // re-read when the window comes back, pending edits pushed when the tab
  // hides or closes — and, via the flushers, before a sign-out ends the
  // right to write. Max never gets it (access.whiteboard), so his login never
  // touches the whiteboard_notes table.
  const wbStaff=user==="admin"&&staff&&staffAccess(staff).whiteboard?staff:null;
  useWhiteboardSync(wbStaff);
  useEffect(function(){regFlush("whiteboard",wbStaff?function(){return flushWhiteboard();}:null);},[wbStaff,regFlush]);
  // Each staff login probes the history tables once (a one-row read), which
  // sends whatever an earlier session left waiting — a first pitch whose file
  // wasn't saved before the logout — instead of waiting for the next call.
  // (Reece only — a limited login never reads the history tables.)
  useEffect(function(){if(user==="admin"&&isSupabaseReady()&&staffAccess(staff).leads)resumeScenarioSync();},[user,staff]);
  useEffect(function(){return function(){sessionGenRef.current++;};},[]);// eslint-disable-line react-hooks/exhaustive-deps

  // ── Supabase Auth: restore session on page load ──
  // If a Supabase auth session is already persisted (from a previous login),
  // skip the login screen and drop the user back into the right view.
  useEffect(()=>{
    if(!isSupabaseReady())return;
    let cancelled=false;
    supabase.auth.getSession().then(({data})=>{
      if(cancelled||!data||!data.session||!data.session.user)return;
      // Reece's or Max's Supabase login → the shared admin app, tagged with
      // who is signed in. Any other email is not staff.
      const who=staffFromEmail(data.session.user.email);
      // Prefs (Practice mode) come from a fresh read of the user — the stored
      // session can carry metadata another device has since changed. The
      // stored copy is shown meanwhile (provisional: the banner is right from
      // the first paint), but until the read lands — or, if it fails, falls
      // back to this session — a limited login writes nothing
      // (isPracticeOrUnknown).
      if(who){primeStaffPrefs(who,data.session.user,true);loadStaffPrefs(who,true);setStaff(who);setUser("admin");}
    });
    // If the session gets revoked elsewhere (admin logs out in another tab,
    // or the JWT expires and refresh fails), bounce back to the login screen.
    // (The [user] reset effect above clears the rest of the session.)
    const {data:sub}=supabase.auth.onAuthStateChange((ev)=>{
      if(ev==="SIGNED_OUT")setUser(null);
    });
    return()=>{cancelled=true;if(sub&&sub.subscription)sub.subscription.unsubscribe();};
  },[]);// eslint-disable-line react-hooks/exhaustive-deps

  // ── Load from Supabase on mount AND whenever the auth'd user changes ──
  // Depending on `user` is load-bearing: RLS now requires an authenticated
  // JWT to read admin-only tables (expenses, sms_log, campaigns, etc.).
  // If we only loaded on mount, the initial fetch would run while the
  // browser is anonymous — those tables come back empty — and then never
  // re-run after admin login, leaving the app showing phantom "no data"
  // even though rows exist in Supabase. Re-running when the Supabase
  // session is established fixes that cleanly.
  //
  // `sbLoaded` is DERIVED during render from which session the data was last
  // loaded for. It used to be a flag set true on the logged-out pass and never
  // reset, so the very commit that logged in already read "loaded": the save
  // effect baselined the pre-load cache and then re-uploaded every table ~2s
  // after the real load landed, the lead prefetch raced the critical load, and
  // the loading skeleton never showed. Now nothing that waits on sbLoaded can
  // run until THIS session's load has been committed.
  const [loadedFor,setLoadedFor]=useState(null);
  const sessKey=(user==="admin"||user==="guest")?(user+":"+(staff||"")+":"+loadNonce):null;
  const sbLoaded=!isSupabaseReady()||sessKey===null||loadedFor===sessKey;
  // The bell's count only once this session's data is in: before that the
  // loans are the cached copy (or the demo seed on a fresh browser) and the
  // badge would show a count that isn't his.
  const fuDueCount=useMemo(function(){return access.dashboard&&sbLoaded?followUpAlertCount(loans,mktgLeads,todayIso):0;},[access.dashboard,sbLoaded,loans,mktgLeads,todayIso]);
  useEffect(()=>{
    if(!isSupabaseReady())return;
    // Don't query Supabase until the user is authenticated. Anon RLS
    // returns empty arrays for the locked-down tables, and a successful
    // empty load would clobber any localStorage-cached state. Bail until
    // setUser("admin") or setUser("guest") fires; the dependency on `user`
    // re-runs this effect at that point.
    if(user!=="admin"&&user!=="guest")return;
    var cancelled=false;
    var key=user+":"+(staff||"")+":"+loadNonce;
    // The save baseline is retaken from the data this load commits.
    lastSavedRef.current={initialized:false};
    const loadAll=loadAllRows;
    const limitedLoad=!staffAccess(staff).leads;
    // Files at all for a limited login: only with its own pipeline (Max's is
    // dormant — no loans read, held, polled or saved for him).
    const limitedFiles=!!staffAccess(staff).pipeline&&!!staffAccess(staff).ownFilesOnly;
    // This session hasn't read the expenses yet (see expensesLoad).
    setExpensesLoad("unknown");
    var expRead=false;   // the expenses read worked (the catch below knows)
    async function load(){
      try{
        if(limitedLoad){
          // A limited login (Max) reads ONLY the shared settings row (rate
          // sheets, HECM limit — read-only for him) and his own expenses —
          // plus, only if his own pipeline is ever switched back on, his own
          // files (RLS returns only his — and anything else is filtered out
          // here too). Never leads, campaigns, borrower accounts, SMS, A/B
          // tests or the touch schedule — and whatever this browser cached
          // for another login is dropped from memory.
          const mine=function(l){return l&&ownerOf(l)===staff;};
          const [settingsR,expR,loansR]=await Promise.all([
            supabase.from('settings').select('*').eq('id','singleton'),
            loadAll('expenses'),
            limitedFiles?loadAll('loans'):Promise.resolve({data:[],error:null}),
          ]);
          if(cancelled)return;
          console.log("LoanFlow Supabase load ("+(staff||"")+", limited): settings + "+(expR.data?expR.data.length:0)+" expenses"+(limitedFiles?" + "+(loansR.data?loansR.data.length:0)+" files":""));
          settingsLoadStateRef.current=settingsR.error?"error":(settingsR.data&&settingsR.data.length>0?"ok":"missing");
          if(!settingsR.error&&settingsR.data&&settingsR.data.length>0){
            adminTablesPollRef.current.settingsSig=rowSig(settingsR.data[0]);
            setSettings(function(prev){var fromDb=settingsFromDb(settingsR.data[0]);if(fromDb.rateSheets)fromDb.rateSheets=mergeRs(fromDb.rateSheets);return Object.assign({},prev,fromDb);});
          }
          const readOk=!expR.error&&!loansR.error;
          // Practice mode just ended and what is saved is back on screen: a
          // save queued before Practice (one that failed, which turning
          // Practice on was confirmed to drop) must never fire later and
          // write that old list back over this one.
          if(discardPracticeRef.current&&readOk){saveGenRef.current++;clearTimeout(saveTimer.current);pendingSaveRef.current=null;latestRunSaveRef.current=null;setSaveStatus({state:"idle",failedTables:[],at:null});}
          // Couldn't read a table: keep only this login's own rows from
          // memory (never another login's cached ones).
          if(!expR.error){setExpenses((expR.data||[]).map(expenseFromDb));expRead=true;}
          else setExpenses(function(prev){return(prev||[]).filter(function(e){return e&&(e.owner||"reece")===staff;});});
          setExpensesLoad(expR.error?"error":"ok");
          if(!loansR.error){
            const own=(loansR.data||[]).map(loanFromDb).filter(mine);
            setLoans(own);
            // Seed the poll's per-row signatures so its first tick doesn't
            // re-replace every file that was just loaded.
            adminTablesPollRef.current.loanSigById=new Map((loansR.data||[]).map(function(r){return[r.id,rowSig(r)];}));
          }
          else setLoans(function(prev){return(prev||[]).filter(mine);});
          if(discardPracticeRef.current){
            // The re-read after Practice mode failed: the practice edits are
            // still in memory, so nothing may save — say so.
            if(readOk)discardPracticeRef.current=false;
            else setSaveStatus({state:"error",failedTables:[(expR.error?"expenses":"files")+" (couldn't re-read after Practice mode — reload the CRM)"],at:Date.now()});
          }
          setMktgLeads([]);setCampaigns([]);setBorrowerAccts([]);setSmsLog([]);setAbTests([]);setTouchSchedule([]);setLeadCounts(null);
          if(!cancelled)setLoadedFor(key);
          return;
        }
        // Each `loadAll` result is { data, error }. If `error` is set we
        // preserve whatever state is already there (localStorage/previous
        // session) so a transient network blip doesn't wipe the UI. If
        // `error` is null we trust the server and set state even when the
        // array is empty — otherwise a genuinely-empty table looks like
        // "stale data that won't clear" and the user never sees accurate
        // state after logout or between accounts.
        const results=await Promise.all([
          loadAll('loans'),
          supabase.from('settings').select('*').eq('id','singleton'),
          // Marketing leads: counts + alert rows only. The full 45K-row
          // table is lazy-loaded on first use — see the leadsReady effect.
          supabase.from('marketing_leads').select('id',{count:'exact',head:true}),
          supabase.from('marketing_leads').select('id',{count:'exact',head:true}).eq('do_not_contact',false),
          supabase.from('marketing_leads').select('*').or(ALERT_LEADS_FILTER).order('created_at',{ascending:false}).limit(200),
          loadAll('campaigns'),
          loadAll('expenses'),
          loadAll('borrower_accounts'),
          loadAll('sms_log'),
          loadAll('ab_tests'),
          loadAll('touch_schedule'),
        ]);
        if(cancelled)return;
        const [loansR,settingsR,leadCountR,contactableR,alertR,campsR,expR,baR,smsR,abR,tsR]=results;
        console.log("LoanFlow Supabase load ("+(user||"anon")+"): "
          +(loansR.data?loansR.data.length:0)+" loans, "
          +(leadCountR.count||0)+" leads (counted, not downloaded), "
          +(alertR.data?alertR.data.length:0)+" alert leads, "
          +(campsR.data?campsR.data.length:0)+" campaigns, "
          +(expR.data?expR.data.length:0)+" expenses, "
          +(smsR.data?smsR.data.length:0)+" sms");

        if(!loansR.error){
          setLoans((loansR.data||[]).map(loanFromDb));
          // Seed the poll's per-row signatures so its first tick doesn't
          // re-replace every loan that was just loaded.
          adminTablesPollRef.current.loanSigById=new Map((loansR.data||[]).map(function(r){return[r.id,rowSig(r)];}));
        }
        settingsLoadStateRef.current=settingsR.error?"error":(settingsR.data&&settingsR.data.length>0?"ok":"missing");
        if(!settingsR.error&&settingsR.data&&settingsR.data.length>0){
          adminTablesPollRef.current.settingsSig=rowSig(settingsR.data[0]);
          setSettings(function(prev){var fromDb=settingsFromDb(settingsR.data[0]);if(fromDb.rateSheets)fromDb.rateSheets=mergeRs(fromDb.rateSheets);return Object.assign({},prev,fromDb);});
        }
        if(!leadCountR.error)   setLeadCounts({total:leadCountR.count||0,contactable:contactableR.error?null:(contactableR.count||0)});
        // Alert rows MERGE into whatever is in memory (same id → server copy)
        // rather than replacing the array, so a working set that is already
        // here can never be collapsed to the ~14 alert rows.
        if(!alertR.error){
          const alertRows=(alertR.data||[]).map(leadFromDb);
          const alertIds=new Set(alertRows.map(function(l){return l.id;}));
          setMktgLeads(function(prev){return(prev||[]).filter(function(l){return l&&!alertIds.has(l.id);}).concat(alertRows);});
        }
        if(!campsR.error)       setCampaigns(campsR.data||[]);
        if(!expR.error){        setExpenses((expR.data||[]).map(expenseFromDb));expRead=true;}
        setExpensesLoad(expR.error?"error":"ok");
        if(!baR.error)          setBorrowerAccts((baR.data||[]).map(baFromDb));
        if(!smsR.error)         setSmsLog((smsR.data||[]).map(smsFromDb));
        if(!abR.error)          setAbTests(abR.data||[]);
        if(!tsR.error)          setTouchSchedule(tsR.data||[]);
      }catch(e){
        console.warn("LoanFlow: Supabase load failed, using localStorage",e);
        // The expenses on screen are this browser's cached copy.
        if(!cancelled&&!expRead)setExpensesLoad("error");
      }
      // Batched with the loaded state above (same tick), so the first render
      // where sbLoaded is true already holds the loaded data.
      if(!cancelled)setLoadedFor(key);
    }
    load();
    return function(){cancelled=true;};
  },[user,staff,loadNonce]);// eslint-disable-line react-hooks/exhaustive-deps

  // Practice mode just turned OFF: what was done during it only ever lived in
  // memory — throw it away and re-read what is really saved (Supabase: the
  // load above runs again; local dev: this browser's saved copy).
  const prevPracticeRef=useRef(practice);
  useEffect(function(){
    const was=prevPracticeRef.current;prevPracticeRef.current=practice;
    if(!was||practice||user!=="admin")return;
    if(isSupabaseReady()){discardPracticeRef.current=true;setLoadNonce(function(n){return n+1;});}
    else{try{const s=loadState();setExpenses((s&&Array.isArray(s.expenses))?s.expenses:[]);setLoans((s&&Array.isArray(s.loans))?s.loans:[]);}catch(e){/* keep memory */}}
  },[practice,user]);

  // ── Light-touch polling for borrower-initiated activity ──
  // The urgent banner needs to react to leads/uploads that arrive while the
  // admin tab is already open, and with two staff logins each tab must see
  // the other's edits. We refresh the alert leads, loans and the settings row
  // every 30 seconds and on tab refocus / window focus. Only rows that
  // actually changed are touched, and anything edited locally but not yet
  // saved is kept (the save merges it with the server copy).
  const adminTablesPollRef=useRef({leadIds:new Set(),loanSigById:new Map(),settingsSig:""});
  // Newest marketing_leads.updated_at this tab has seen (server clock — the
  // ml_touch_updated_at trigger stamps it). The poll re-reads a little before
  // it, so a write that committed late is still caught.
  const leadPollMarkRef=useRef("");
  const leadsReadyRef=useRef(false);
  useEffect(function(){leadsReadyRef.current=leadsReady;},[leadsReady]);
  // Take server copies of leads already in memory that this tab has NOT
  // edited (locally edited rows differ from the save baseline and keep their
  // edits — the save merges the server copy in). Rows not in memory are left
  // alone. Returns how many were refreshed.
  const refreshCleanLeads=function(serverRows){
    const cur=leadsRef.current||[];
    const curById=new Map();cur.forEach(function(l){if(l&&l.id)curById.set(l.id,l);});
    const L=lastSavedRef.current;
    const baseArr=Array.isArray(L.mktgLeads)?L.mktgLeads:[];
    const baseById=new Map();baseArr.forEach(function(l){if(l&&l.id)baseById.set(l.id,l);});
    const refreshed=new Map();
    (serverRows||[]).forEach(function(srv){
      const mine=srv&&curById.get(srv.id);if(!mine)return;
      if(L.initialized&&baseById.get(srv.id)!==mine)return;
      const next=leadFromDb(srv);
      const a=leadToDb(next),b=leadToDb(mine);delete a.updated_at;delete b.updated_at;
      if(seq(a,b))return;
      refreshed.set(srv.id,{from:mine,to:next});
    });
    if(!refreshed.size)return 0;
    setMktgLeads(function(prev){return prev.map(function(l){const rp=l&&refreshed.get(l.id);return(rp&&rp.from===l)?rp.to:l;});});
    if(L.initialized&&Array.isArray(L.mktgLeads)){
      L.mktgLeads=L.mktgLeads.map(function(l){const rp=l&&refreshed.get(l.id);return(rp&&rp.from===l)?rp.to:l;});
    }
    return refreshed.size;
  };
  const settingsRef=useRef(settings);useEffect(()=>{settingsRef.current=settings;},[settings]);
  const settingsSaveInflightRef=useRef(false);
  // Adopt a newer server settings row into this tab. Local edits not yet
  // saved are rebased on top of it (three-way), never thrown away.
  const adoptServerSettings=function(row){
    const fromDb=settingsFromDb(row);
    if(fromDb.rateSheets)fromDb.rateSheets=mergeRs(fromDb.rateSheets);
    const st=settingsLoadStateRef.current;
    const L=lastSavedRef.current;
    if(st!=="ok"&&st!=="missing"){
      // The login read failed — this tab is on cached/default settings and
      // has been blocked from writing them. Take the server copy whole.
      const next=Object.assign({},settingsRef.current,fromDb);
      settingsLoadStateRef.current="ok";
      setSettings(next);
      if(L.initialized)L.settings=next;
      console.log("LoanFlow poll: settings recovered from the server");
      return;
    }
    if(!L.initialized)return;
    const base=L.settings||settingsRef.current;
    const srvApp=Object.assign({},base,fromDb);
    if(seq(srvApp,base))return;
    L.settings=srvApp;
    setSettings(function(prev){return prev===base?srvApp:mergeSettings3(prev,base,srvApp);});
    console.log("LoanFlow poll: settings changed elsewhere — refreshed (rate sheets / swap / other user)");
  };
  useEffect(function(){
    if(!isSupabaseReady()||user!=="admin"||!sbLoaded)return;
    let cancelled=false;
    let lastRun=0;
    // A limited login (Max) polls the shared settings row (Reece's rate
    // sheet / market updates reach his calculator) — never leads, and files
    // (another tab or device of his) only with his own pipeline, which is
    // dormant: no loans poll for him.
    const limitedPoll=!staffAccess(staff).leads;
    const ownOnlyPoll=!!staffAccess(staff).pipeline&&!!staffAccess(staff).ownFilesOnly;
    // Take the loan rows the server changed since the last tick into this
    // tab — except ones edited here and not yet saved (the save merges the
    // server copy into those).
    function applyLoanRows(rows){
      // Per-row signature: id → updated_at + content hash. updated_at
      // alone never moved before the loans_touch_updated_at trigger, so
      // a borrower upload / statement / callback or Max's edit on an
      // EXISTING file stayed invisible until a reload.
      const prevSig=adminTablesPollRef.current.loanSigById||new Map();
      const nextSig=new Map();const changedRows=[];
      rows.forEach(function(r){const sg=rowSig(r);nextSig.set(r.id,sg);if(prevSig.get(r.id)!==sg)changedRows.push(r);});
      adminTablesPollRef.current.loanSigById=nextSig;
      if(changedRows.length>0){
        // Max's tab never takes in anyone else's file, whatever comes back.
        const srvLoans=changedRows.map(loanFromDb).filter(function(l){return!ownOnlyPoll||ownerOf(l)===staff;});
        const srvById=new Map(srvLoans.map(function(l){return[l.id,l];}));
        const L=lastSavedRef.current;
        const baseLoans=Array.isArray(L.loans)?L.loans:[];
        const baseLoanById=new Map();baseLoans.forEach(function(l){if(l&&l.id)baseLoanById.set(l.id,l);});
        // A loan edited here and not yet saved differs from the baseline:
        // keep it (and its baseline) so the save merges the server copy in.
        const replace=new Map();const have=new Set();let kept=0;
        (loansRef.current||[]).forEach(function(l){
          if(!l||!l.id)return;have.add(l.id);
          const sv=srvById.get(l.id);if(!sv)return;
          if(L.initialized&&baseLoanById.get(l.id)!==l){kept++;return;}
          replace.set(l.id,{from:l,to:sv});
        });
        const added=[];srvById.forEach(function(sv,id){if(!have.has(id))added.push(sv);});
        if(replace.size||added.length){
          setLoans(function(prev){
            const seen=new Set();
            // Only swap the exact object judged clean above — if it changed
            // in the meantime it is a fresh local edit and stays.
            const out=prev.map(function(l){if(!l||!l.id)return l;seen.add(l.id);const rp=replace.get(l.id);return(rp&&rp.from===l)?rp.to:l;});
            added.forEach(function(sv){if(!seen.has(sv.id))out.push(sv);});
            return out;
          });
          // Keep the save baseline in lockstep so these server-sourced rows
          // aren't immediately re-uploaded by the dirty-diff.
          if(L.initialized){
            const inBase=new Set();
            const nb=baseLoans.map(function(l){if(l&&l.id)inBase.add(l.id);const rp=l&&replace.get(l.id);return rp?rp.to:l;});
            replace.forEach(function(rp,id){if(!inBase.has(id))nb.push(rp.to);});
            added.forEach(function(sv){if(!inBase.has(sv.id))nb.push(sv);});
            L.loans=nb;
          }
          console.log("LoanFlow poll: refreshed "+replace.size+" loan(s), "+added.length+" new"+(kept?", kept "+kept+" locally-edited":""));
        } else if(kept)console.log("LoanFlow: poll kept "+kept+" locally-edited loan(s) over the server copy");
      }
    }
    async function refresh(){
      lastRun=Date.now();
      try{
        if(limitedPoll){
          const [sr,lr]=await Promise.all([
            supabase.from('settings').select('*').eq('id','singleton'),
            ownOnlyPoll?loadAllRows('loans'):Promise.resolve(null),
          ]);
          if(cancelled)return;
          if(sr&&!sr.error&&Array.isArray(sr.data)&&sr.data.length){
            const sg=rowSig(sr.data[0]);
            if(sg!==adminTablesPollRef.current.settingsSig){adminTablesPollRef.current.settingsSig=sg;adoptServerSettings(sr.data[0]);}
          }
          if(lr&&!lr.error&&lr.data)applyLoanRows(lr.data);
          return;
        }
        // Never the full lead table — at 45K rows the old full-table poll
        // re-downloaded the entire database every 30 seconds. Two slices:
        // the alert rows (inbound portal leads + opt-outs, new ones appended),
        // and leads the OTHER user (or the server) changed since the last poll —
        // any lead, not just alert rows — so this tab's copy of a worked lead
        // never goes stale (its next write merges anyway; this is so he SEES
        // it). Read a few minutes behind the newest stamp seen; paged so a
        // bulk import (1000 rows share one stamp) can't wedge it.
        const mark=leadPollMarkRef.current||new Date(Date.now()-10*60*1000).toISOString();
        const since=new Date((Date.parse(mark)||Date.now())-2*60*1000).toISOString();
        const loadUpdatedLeads=async function(){
          let all=[];
          for(let p=0;p<5;p++){
            const r=await supabase.from('marketing_leads').select('*').gte('updated_at',since).order('updated_at',{ascending:true}).order('id',{ascending:true}).range(p*1000,p*1000+999);
            if(r.error)return{data:all,error:r.error};
            all=all.concat(r.data||[]);
            if(!r.data||r.data.length<1000)break;
          }
          return{data:all,error:null};
        };
        const [leadsR,loansR,settingsR,updR]=await Promise.all([
          supabase.from('marketing_leads').select('*').or(ALERT_LEADS_FILTER).order('created_at',{ascending:false}).limit(200),
          loadAllRows('loans'),
          supabase.from('settings').select('*').eq('id','singleton'),
          loadUpdatedLeads(),
        ]);
        if(cancelled)return;
        if(updR&&!updR.error&&updR.data){
          const n=refreshCleanLeads(updR.data);
          if(n>0)console.log("LoanFlow poll: refreshed "+n+" lead(s) changed elsewhere");
          // Advance the mark only once the working set is in memory — a row
          // changed during that download would otherwise be skipped for good.
          if(leadsReadyRef.current){
            let mx=mark,mxT=Date.parse(mark)||0;
            updR.data.forEach(function(r){const t=r&&r.updated_at?Date.parse(r.updated_at)||0:0;if(t>mxT){mxT=t;mx=String(r.updated_at);}});
            leadPollMarkRef.current=mx;
          }
        }
        if(!leadsR.error&&leadsR.data){
          const known=adminTablesPollRef.current.leadIds;
          // Rows the server UPDATED (intake step 2, opt-out, MagicBlocks) for
          // leads already in memory. Refresh a row when the server copy
          // differs AND the admin hasn't touched it locally (locally dirty =
          // differs from the save baseline; those keep their edits).
          {
            const n=refreshCleanLeads(leadsR.data);
            if(n>0)console.log("LoanFlow poll: refreshed "+n+" alert lead(s) updated server-side");
          }
          const newOnes=leadsR.data.filter(function(l){return!known.has(l.id);});
          if(newOnes.length>0){
            newOnes.forEach(function(l){known.add(l.id);});
            // Append only rows not already in state (the full lazy load may
            // have brought them in). Never replace the array — that would
            // clobber in-flight admin edits.
            const have=new Set((leadsRef.current||[]).map(function(x){return x.id;}));
            const add=newOnes.filter(function(l){return!have.has(l.id);}).map(leadFromDb);
            if(add.length>0){
              setMktgLeads(function(prev){return prev.concat(add);});
              // Keep the save baseline in lockstep so server-sourced rows
              // aren't immediately re-uploaded by the dirty-diff.
              if(lastSavedRef.current.initialized&&Array.isArray(lastSavedRef.current.mktgLeads)){
                lastSavedRef.current.mktgLeads=lastSavedRef.current.mktgLeads.concat(add);
              }
            }
          }
        }
        if(!loansR.error&&loansR.data)applyLoanRows(loansR.data);
        // Settings changed elsewhere (another tab/device, Max, or a SQL rate
        // sheet load). Skipped while this tab's own settings write is in
        // flight — that write merges with the server copy itself.
        if(settingsR&&!settingsR.error&&Array.isArray(settingsR.data)&&settingsR.data.length&&!settingsSaveInflightRef.current){
          const sg=rowSig(settingsR.data[0]);
          if(sg!==adminTablesPollRef.current.settingsSig){
            adminTablesPollRef.current.settingsSig=sg;
            adoptServerSettings(settingsR.data[0]);
          }
        }
      }catch(e){/* network blip — try again next tick */}
    }
    // Initial seed so the first poll doesn't show every existing lead as
    // "new" (we already loaded them on mount).
    const seed=function(){
      try{
        const leadIds=new Set((leadsRef.current||[]).map(function(l){return l.id;}));
        adminTablesPollRef.current.leadIds=leadIds;
      }catch(e){}
    };
    seed();
    const onVis=function(){if(!document.hidden)refresh();};
    // Window focus (back from another app) too — throttled so a focus right
    // after a visibility refresh doesn't fetch twice.
    const onFocus=function(){if(Date.now()-lastRun>5000)refresh();};
    document.addEventListener("visibilitychange",onVis);
    window.addEventListener("focus",onFocus);
    const t=setInterval(refresh,30000);
    return function(){
      cancelled=true;
      document.removeEventListener("visibilitychange",onVis);
      window.removeEventListener("focus",onFocus);
      clearInterval(t);
    };
  },[user,sbLoaded,staff]);// eslint-disable-line react-hooks/exhaustive-deps

  // ── Merge server lead rows into state without losing local edits ──
  // Downloads take a while. Anything the admin edited in the meantime is a row
  // whose in-memory object differs from the save baseline — keep those (and
  // leave them OUT of the baseline so the next save still writes them). Rows
  // the server doesn't know about yet (created seconds ago) are kept too.
  // Letting the server copy simply replace everything was the "I click Mark
  // reviewed and it comes back" bug.
  function mergeServerLeads(serverArr){
    const prevLeads=leadsRef.current||[];
    const baseArr=Array.isArray(lastSavedRef.current.mktgLeads)?lastSavedRef.current.mktgLeads:[];
    const baseById=new Map();baseArr.forEach(function(l){if(l&&l.id)baseById.set(l.id,l);});
    const localDirty=new Map();
    if(lastSavedRef.current.initialized)prevLeads.forEach(function(l){if(l&&l.id&&baseById.get(l.id)!==l)localDirty.set(l.id,l);});
    const serverIds=new Set(serverArr.map(function(l){return l.id;}));
    const merged=serverArr.map(function(l){return localDirty.has(l.id)?localDirty.get(l.id):l;});
    const extras=prevLeads.filter(function(l){return!serverIds.has(l.id);});
    setMktgLeads(extras.length>0?merged.concat(extras):merged);
    if(localDirty.size>0)console.log("LoanFlow: kept "+localDirty.size+" locally-edited lead(s) over the server copy");
    // Baseline = server rows + extras that were already saved (unchanged vs the
    // old baseline), so neither is re-uploaded; dirty rows stay dirty. A dirty
    // row keeps its OLD baseline entry (when it had one): it is still sent,
    // and the save's three-way merge needs to know what this tab started from.
    const cleanExtras=extras.filter(function(l){return baseById.get(l.id)===l;});
    const dirtyBases=[];localDirty.forEach(function(l,id){const b=baseById.get(id);if(b)dirtyBases.push(b);});
    lastSavedRef.current.mktgLeads=serverArr.filter(function(l){return!localDirty.has(l.id);}).concat(cleanExtras,dirtyBases);
  }
  // Load EVERY lead (including never-mailed) on request. Resolves true once
  // the full table is in memory. Concurrent callers share one download.
  const loadAllLeads=useCallback(function(){
    if(limitedRef.current)return Promise.resolve(false);   // not on a limited login
    if(allLeadsLoaded||!isSupabaseReady())return Promise.resolve(true);
    if(allLeadsPromiseRef.current)return allLeadsPromiseRef.current;
    // Belongs to this session only — a logout mid-download discards it.
    const gen=sessionGenRef.current;
    const stale=function(){return sessionGenRef.current!==gen;};
    const p=(async function(){
      let r=null;
      for(const wait of [0,2000,5000]){
        if(wait)await new Promise(function(res){setTimeout(res,wait);});
        if(stale())return false;
        r=await loadAllRows('marketing_leads',null,function(loaded,total){if(!stale())setLeadLoadProgress({loaded:loaded,total:total,all:true});});
        if(stale())return false;
        if(!r.error)break;
      }
      setLeadLoadProgress(null);
      if(r.error){console.warn("LoanFlow: full lead load failed",r.error.message||r.error);if(allLeadsPromiseRef.current===p)allLeadsPromiseRef.current=null;return false;}
      const serverArr=(r.data||[]).map(leadFromDb);
      mergeServerLeads(serverArr);
      setLeadCounts({total:serverArr.length,contactable:serverArr.filter(function(l){return!l.doNotContact;}).length});
      setAllLeadsLoaded(true);setLeadsReady(true);
      console.log("LoanFlow: full marketing database loaded on request — "+serverArr.length+" leads");
      return true;
    })();
    allLeadsPromiseRef.current=p;
    return p;
  },[allLeadsLoaded]);// eslint-disable-line react-hooks/exhaustive-deps
  // Bring specific server rows (e.g. from a server-side search) into memory so
  // edits to them save normally. They join the baseline too: adopting a row is
  // not an edit and must not trigger an upload.
  const adoptLeads=useCallback(function(rows){
    const have=new Set((leadsRef.current||[]).map(function(l){return l.id;}));
    // A fresh fetch of a lead already in memory replaces this tab's copy when
    // it hasn't been edited here — it used to be ignored, leaving the stale copy.
    const inMem=(rows||[]).filter(function(l){return l&&l.id&&have.has(l.id);});
    if(inMem.length)refreshCleanLeads(inMem);
    const add=(rows||[]).filter(function(l){return l&&l.id&&!have.has(l.id);});
    if(!add.length)return;
    setMktgLeads(function(prev){const h=new Set(prev.map(function(l){return l.id;}));return prev.concat(add.filter(function(l){return!h.has(l.id);}));});
    if(lastSavedRef.current.initialized&&Array.isArray(lastSavedRef.current.mktgLeads))lastSavedRef.current.mktgLeads=lastSavedRef.current.mktgLeads.concat(add);
  },[]);

  // ── Background prefetch of the WORKING lead set ──
  // (Mailed / worked / flagged leads — see WORKING_LEADS_FILTER. The ~74K
  // never-mailed leads are NOT downloaded here; loadAllLeads fetches them.)
  // Kicks off as soon as the admin is authenticated and the critical initial
  // load (counts + alert rows + loans) has finished — NOT gated on which view
  // is open. The dashboard still paints instantly from counts; meanwhile the
  // complete marketing_leads table streams into memory in the background, so
  // by the time the admin reaches Inquiry / Marketing / Settings the leads are
  // already searchable with no empty-results gap. Runs exactly once per
  // session — the `leadsReady` guard makes every later render a no-op, and the
  // in-memory list is reused across every page (no per-page reload). Only a
  // browser refresh, logout, or idle-timeout clears it and reloads.
  //
  // Cancellation is tied to the SESSION, not to this effect's runs: a nav
  // click used to cancel the in-flight download (nav is a dep), the re-run
  // bailed on the in-flight guard, and the finished ~38K-row download was
  // thrown away — Outbound sat on "Loading mail history…" until another click.
  useEffect(function(){
    if(!isSupabaseReady()){setLeadsReady(true);setAllLeadsLoaded(true);return;}// local dev: localStorage state is already complete
    if(user!=="admin"||leadsReady)return;
    if(limitedRef.current)return;// a limited login (Max) never downloads leads
    if(!sbLoaded)return;// let the critical initial load land first so login/dashboard stay instant
    if(leadsLoadingRef.current)return;
    const token={};
    leadsLoadingRef.current=token;
    const gen=sessionGenRef.current;
    const cancelledFn=function(){return sessionGenRef.current!==gen;};
    const release=function(){if(leadsLoadingRef.current===token)leadsLoadingRef.current=false;};
    (async function(){
      // Up to 4 attempts with backoff. A 70K-row download has real odds of
      // one page hitting a transient blip; the old code gave up entirely and
      // only retried if the admin happened to change views — which read as
      // "leads never load" in practice. Any error means retry: a partial
      // table must never be accepted as the full dataset (totals would lie
      // and the save baseline would drift).
      const delays=[0,2000,5000,10000];
      let r=null;
      for(let attempt=0;attempt<delays.length;attempt++){
        if(delays[attempt]>0)await new Promise(function(res){setTimeout(res,delays[attempt]);});
        if(cancelledFn()){release();return;}
        r=await loadAllRows('marketing_leads',null,function(loaded,total){
          if(!cancelledFn())setLeadLoadProgress({loaded:loaded,total:total});
        },WORKING_LEADS_FILTER);
        if(cancelledFn()){release();return;}
        if(!r.error)break;
        console.warn("LoanFlow: lead load attempt "+(attempt+1)+" failed",r.error.message||r.error);
      }
      if(r.error){
        // Persistent failure after retries. Clear the in-flight guard so the
        // effect can try again on its next run (e.g. after a re-auth or when
        // sbLoaded flips), rather than wedging leads as permanently unloaded.
        console.warn("LoanFlow: background lead load failed after retries",r.error.message||r.error);
        setLeadLoadProgress(null);
        release();
        return;
      }
      const serverArr=(r.data||[]).map(leadFromDb);
      mergeServerLeads(serverArr);
      setLeadLoadProgress(null);
      setLeadsReady(true);
      release();
      console.log("LoanFlow: working leads loaded — "+serverArr.length+" (mailed/worked); never-mailed leads stay in the database until requested");
    })();
    // No cleanup cancel: sbLoaded fires the prefetch right after login; nav
    // is kept only as a retry nudge if that prefetch FAILED (the in-flight
    // and leadsReady guards make every other run a no-op).
  },[user,sbLoaded,nav,leadsReady]);// eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist to localStorage + Supabase whenever core state changes ──
  // Save-state machine that drives the UI indicator. `idle` is the resting
  // state. `pending` means a change has occurred and the debounce timer is
  // counting down. `saving` means the upsert is in flight. `saved` is a
  // transient confirmation that auto-clears. `partial` means it was written
  // but some fields could NOT be stored because a migration hasn't been run
  // (it names the SQL file and stays until it is). `error` sticks until the
  // next successful save so the user can't miss it.
  const [saveStatus,setSaveStatus]=useState({state:"idle",failedTables:[],at:null});
  const saveStatusRef=useRef("idle");useEffect(()=>{saveStatusRef.current=saveStatus.state;},[saveStatus.state]);
  // Database columns found missing this session (e.g. "expenses.notes").
  const [schemaWarn,setSchemaWarn]=useState([]);
  const saveTimer=useRef(null);
  const lsSaveTimer=useRef(null);
  // Bumped every time a new debounced save is scheduled. A failed save's
  // automatic retry checks it first, so a retry can never write a stale
  // snapshot over an edit made after the failure.
  const saveGenRef=useRef(0);
  // "unknown" until the settings row has been read this session; "ok" once
  // loaded, "missing" if the table is empty (fresh install), "error" if the
  // read failed. The settings upsert is gated on ok/missing — see the save.
  const settingsLoadStateRef=useRef("unknown");
  // Per-table dirty tracking. After the initial Supabase load we baseline
  // these refs so the loaded state isn't immediately re-uploaded. After each
  // subsequent successful save we update the ref. On every render we compare
  // current state references to the baseline; only changed tables get sent.
  // This is the difference between a 1-second save and a 30-second save when
  // the marketing DB has 20K+ leads. The baseline is also the BASE of the
  // three-way merges (loans, settings).
  const lastSavedRef=useRef({initialized:false});
  // Hooks so logout / the idle timeout can push a pending edit out first.
  const pendingSaveRef=useRef(null);    // debounced save still waiting to fire
  const latestRunSaveRef=useRef(null);  // newest save closure (re-run after an error)
  const saveInflightRef=useRef(null);   // promise of the save in flight → true/false
  // EVERY save still on its way (a debounced save can start while an older
  // one is still writing). An expense delete waits for them first.
  const saveInflightSetRef=useRef(new Set());
  // Expense ids this session tried to insert that were already another
  // device's rows on the server (insertNewExpenses' _collided — ours gets
  // renumbered): a delete of OUR row under that old id must never reach the
  // server, where the id is someone else's row.
  const expForeignIdsRef=useRef(new Set());
  // Fingerprints of expense rows this session tried to INSERT — tells "our own
  // earlier attempt already landed" apart from "another device used this id".
  const sentExpenseFpRef=useRef(Object.create(null));
  // Same for new pipeline files (see saveLoansMerged).
  const sentLoanFpRef=useRef(Object.create(null));
  const expensesRef=useRef(expenses);useEffect(()=>{expensesRef.current=expenses;},[expenses]);
  // Warn user before closing/refreshing if there's a pending debounce save.
  useEffect(()=>{
    // "error" is included on purpose: a failed cloud save leaves the edit only
    // in this tab's memory/localStorage, and the next login replaces that with
    // whatever the server has — so leaving now loses the change.
    if(saveStatus.state!=="pending"&&saveStatus.state!=="saving"&&saveStatus.state!=="error")return;
    const handler=(e)=>{
      e.preventDefault();
      e.returnValue=saveStatus.state==="error"
        ?"Your last change FAILED to save to the cloud. If you leave now it will be lost — check your connection and make another edit to retry."
        :"You have unsaved changes that haven't finished syncing to the cloud. Are you sure you want to leave?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload",handler);
    return()=>window.removeEventListener("beforeunload",handler);
  },[saveStatus.state]);
  // Push any unsaved change to the cloud NOW (skip the 2s debounce) and wait
  // for it. Resolves true when nothing is left unsaved. Used before logout and
  // the idle timeout — both used to silently drop an edit made in the last 2s
  // (or one whose save had failed).
  const flushPendingSave=async function(){
    try{
      if(pendingSaveRef.current){
        clearTimeout(saveTimer.current);
        const fn=pendingSaveRef.current;pendingSaveRef.current=null;
        return await fn(1);
      }
      if(saveInflightRef.current&&await saveInflightRef.current)return true;
      if(saveStatusRef.current==="error"&&latestRunSaveRef.current)return await latestRunSaveRef.current(1);
      return saveStatusRef.current!=="error";
    }catch(e){return false;}
  };
  // Wait for every save that is on its way right now (not ones started later).
  const awaitSavesInFlight=async function(){
    const pending=Array.from(saveInflightSetRef.current);
    if(pending.length)await Promise.all(pending.map(function(p){return Promise.resolve(p).catch(function(){return false;});}));
  };
  // ── Expenses: the server's copy, for Import and deletes ──
  // Import's Add plans against what the SERVER holds — this tab's list may be
  // this browser's stale cache (the login read failed) and expenses aren't
  // polled. This pushes this tab's own unsaved edits out first, waits for
  // every save on its way, then re-reads the signed-in person's rows.
  // → {ok, list} | {ok:false, error}; local dev (no server): {ok, list:null}
  // (the list in memory IS the record there).
  const readServerExpenses=async function(){
    if(!isSupabaseReady())return{ok:true,list:null};
    const sess=sessionGenRef.current;
    const who=staffKey;
    const flushed=await flushPendingSave();
    await awaitSavesInFlight();
    if(!flushed)return{ok:false,error:"a change on this screen hasn't saved to the cloud yet"};
    let r;
    try{r=await loadAllRows('expenses');}catch(e){r={data:[],error:e};}
    if(sessionGenRef.current!==sess)return{ok:false,error:"signed out"};
    if(!r||r.error)return{ok:false,error:(r&&r.error&&(r.error.message||String(r.error)))||"no answer"};
    setExpensesLoad("ok");
    return{ok:true,list:(r.data||[]).map(expenseFromDb).filter(function(e){return e&&(e.owner||"reece")===who;})};
  };
  // Before an expense delete reaches the server: the rows are already out of
  // the list, so no save started from now on carries them — but one already
  // ON ITS WAY does (right after an import: the insert of the new rows and
  // the upsert of the flagged ones). A delete that landed first matched
  // nothing / was undone, and the rows came back at the next login. Waits
  // for those, then drops ids that are another device's rows on the server.
  const beforeExpenseDelete=async function(ids){
    await awaitSavesInFlight();
    const foreign=expForeignIdsRef.current;
    return(ids||[]).filter(function(id){return!foreign.has(id);});
  };
  // An expense id this tab minted was already taken on the server (another
  // device added one meanwhile). Renumber ours past the server's highest and
  // let the next save insert it — never overwrite the other row.
  const remintExpenseIds=async function(ids){
    try{
      const groups={};
      ids.forEach(function(id){const m=/^([A-Za-z]+)-(\d+)$/.exec(id||"");const p=m?m[1]:"EXP";(groups[p]=groups[p]||[]).push(id);});
      const serverMax={};
      for(const p of Object.keys(groups)){
        const q=await supabase.from('expenses').select('id').like('id',p+'-%');
        if(q.error){console.warn("[LoanFlow save] couldn't renumber colliding expense ids",q.error.message||q.error);return;}
        let mx=0;(q.data||[]).forEach(function(r){const m=/-(\d+)$/.exec(r.id||"");if(m)mx=Math.max(mx,parseInt(m[1],10));});
        serverMax[p]=mx;
      }
      const mx=Object.assign({},serverMax);
      (expensesRef.current||[]).forEach(function(e){const m=/^([A-Za-z]+)-(\d+)$/.exec((e&&e.id)||"");if(m&&mx[m[1]]!==undefined)mx[m[1]]=Math.max(mx[m[1]],parseInt(m[2],10));});
      const map={};
      Object.keys(groups).forEach(function(p){groups[p].forEach(function(old){mx[p]++;map[old]=p+"-"+String(mx[p]).padStart(4,"0");});});
      setExpenses(function(prev){return prev.map(function(e){return(e&&map[e.id])?Object.assign({},e,{id:map[e.id]}):e;});});
      const renamed=Object.keys(map).map(function(k){return k+" → "+map[k];});
      console.warn("[LoanFlow save] expense number(s) already used on another device — renumbered: "+renamed.join(", "));
      setCallFlash("Expense number already used on another device — saved as "+Object.keys(map).map(function(k){return map[k];}).join(", "));
      setTimeout(function(){setCallFlash("");},7000);
    }catch(e){console.warn("[LoanFlow save] expense renumber failed",e);}
  };
  // A new file's id was already taken on the server by someone else's file
  // (another device minted the same number before this tab saw it). The other
  // file is never touched: ours is renumbered past the server's highest in the
  // same series, its links follow (the open file, the lead it came from), and
  // the next save inserts it. The poll then brings the other file in.
  const remintLoanIds=async function(ids){
    try{
      const groups={};
      ids.forEach(function(id){const m=/^(LN-M?|[A-Za-z]+-)(\d+)$/.exec(id||"");const p=m?m[1]:"LN-";(groups[p]=groups[p]||[]).push(id);});
      const mx={};
      for(const p of Object.keys(groups)){
        const q=await supabase.from('loans').select('id').like('id',p+'%');
        if(q.error){console.warn("[LoanFlow save] couldn't renumber colliding file ids",q.error.message||q.error);return;}
        const re=new RegExp("^"+p.replace(/[-]/g,"\\-")+"(\\d+)$");
        let n=0;(q.data||[]).concat(loansRef.current||[]).forEach(function(r){const m=re.exec((r&&r.id)||"");if(m)n=Math.max(n,parseInt(m[1],10));});
        mx[p]=n;
      }
      const map={};
      Object.keys(groups).forEach(function(p){groups[p].forEach(function(old){mx[p]++;map[old]=p+String(mx[p]).padStart(3,"0");});});
      // Which leads point at OUR file (not the other one under the same id).
      const srcLead={};
      (loansRef.current||[]).forEach(function(l){if(l&&map[l.id]&&l.sourceLeadId)srcLead[l.sourceLeadId]=l.id;});
      // The poll already holds the other file's row signature — forget it so
      // the next poll adds that file instead of treating it as unchanged.
      Object.keys(map).forEach(function(old){try{adminTablesPollRef.current.loanSigById.delete(old);}catch(e){}});
      setLoans(function(prev){return prev.map(function(l){return(l&&map[l.id])?Object.assign({},l,{id:map[l.id]}):l;});});
      setSel(function(s){return(s&&map[s.id])?Object.assign({},s,{id:map[s.id]}):s;});
      const nowIso=new Date().toISOString();
      setMktgLeads(function(prev){return prev.map(function(m){
        if(!m||!m.converted_loan_id||!map[m.converted_loan_id]||srcLead[m.id]!==m.converted_loan_id)return m;
        return Object.assign({},m,{converted_loan_id:map[m.converted_loan_id],updated_at:nowIso});
      });});
      const renamed=Object.keys(map).map(function(k){return k+" → "+map[k];});
      console.warn("[LoanFlow save] file number(s) already used by another file — renumbered: "+renamed.join(", "));
      setCallFlash("File number already in use by another file — saved as "+Object.keys(map).map(function(k){return map[k];}).join(", "));
      setTimeout(function(){setCallFlash("");},7000);
    }catch(e){console.warn("[LoanFlow save] file renumber failed",e);}
  };
  // Settings write = re-read the row, three-way merge, write the result.
  const saveSettingsMerged=async function(local,base){
    const rd=await supabase.from('settings').select('*').eq('id','singleton');
    if(rd.error)return{error:rd.error};
    let merged=local;
    if(Array.isArray(rd.data)&&rd.data.length){
      const fromDb=settingsFromDb(rd.data[0]);
      if(fromDb.rateSheets)fromDb.rateSheets=mergeRs(fromDb.rateSheets);
      merged=mergeSettings3(local,base,Object.assign({},base||local,fromDb));
    }
    const w=await supabase.from('settings').upsert(settingsToDb(merged));
    return Object.assign({},w,{_merged:merged});
  };
  useEffect(()=>{
    // Debounced save to localStorage (prevents lag with large datasets).
    // Only while someone is logged in: doLogout wipes the in-memory tables,
    // and saving that wiped state would overwrite the cache — every
    // subsequent login then started from a blank screen until the full
    // Supabase load finished instead of painting instantly from cache.
    clearTimeout(lsSaveTimer.current);
    // A limited login never writes this browser's cache while connected (it
    // holds no one else's tables, and the cache paints Reece's next login),
    // and in Practice mode nothing is written at all.
    var skipLocal=limited&&(isSupabaseReady()||isPracticeOrUnknown(staffKey));
    if(user&&!skipLocal){
      lsSaveTimer.current=setTimeout(()=>{
        saveState({loans,mktgLeads,campaigns,expenses,borrowerAccts,smsLog,settings,abTests,touchSchedule,mailTemplates});
      },500);
    }
    // Debounced save to Supabase — ONLY when the admin is authenticated.
    // Writing with the anon key (or after logout) would either fail RLS
    // silently or, worse, overwrite good Supabase rows with stale/default
    // in-memory state. Admin auth is the single gate for server-side writes.
    if(!isSupabaseReady()||!sbLoaded)return;
    if(user!=="admin")return;
    if(limited&&discardPracticeRef.current)return;   // Practice mode just ended — the re-read replaces all of this
    // Per-table dirty check. Reference-equality vs the last-saved snapshot
    // tells us which tables actually changed. React gives us new refs from
    // every setState, so this catches every real edit while ignoring renders
    // triggered by unrelated state. Without this, a single keystroke in any
    // input would re-upsert all 20K+ marketing leads.
    // A limited login (Max) writes ONLY his own expenses — and his own files
    // only if his own pipeline (pipeline + ownFilesOnly, dormant since 10/2)
    // is ever switched back on. Never settings, leads, anyone else's files or
    // anything else (RLS refuses them too).
    var limitedSave=limited;
    var ownOnlySave=ownOnly;
    var current=limitedSave?(ownOnlySave?{expenses:expenses,loans:loans}:{expenses:expenses}):{loans:loans,mktgLeads:mktgLeads,campaigns:campaigns,expenses:expenses,borrowerAccts:borrowerAccts,smsLog:smsLog,settings:settings,abTests:abTests,touchSchedule:touchSchedule};
    var last=lastSavedRef.current;
    if(!last.initialized){
      // Just-loaded state — baseline, don't fire a redundant save. (sbLoaded
      // only turns true on the render that commits THIS session's load.)
      lastSavedRef.current=Object.assign({initialized:true},current);
      return;
    }
    // Practice mode (or a limited login whose prefs haven't been read yet):
    // nothing is written. Turning it off re-reads the saved data instead.
    if(limitedSave&&isPracticeOrUnknown(staffKey))return;
    var dirty={};
    Object.keys(current).forEach(function(k){if(current[k]!==last[k])dirty[k]=true;});
    if(Object.keys(dirty).length===0)return;
    // Flag "pending" the moment a change arrives so the UI shows state even
    // during the 2-second debounce window. If no further changes come in,
    // `saving` → `saved` follows naturally.
    setSaveStatus(function(s){return s.state==="error"?s:{state:"pending",failedTables:[],at:Date.now()};});
    clearTimeout(saveTimer.current);
    var gen=++saveGenRef.current;
    var sessGen=sessionGenRef.current;
    var doSave=async function(attempt){
      // Practice mode turned on after this was scheduled (a retry): drop it.
      // An earlier FAILED save stays on the pill — that change never saved,
      // and clearing the warning would hide it.
      if(limitedSave&&isPracticeOrUnknown(staffKey)){setSaveStatus(function(s){return s.state==="error"?s:{state:"idle",failedTables:[],at:null};});return true;}
      setSaveStatus({state:"saving",failedTables:[],at:Date.now()});
      var L=lastSavedRef.current;
      // Baseline refs at the START of this save. On success only what this
      // save actually sent is marked clean: a baseline that moved mid-save
      // (working-set download merge, adoptLeads, the poll) is kept, instead of
      // being reset to this save's old snapshot — which re-uploaded every lead.
      var startBase={};Object.keys(current).forEach(function(k){startBase[k]=L[k];});
      var sent={};
      // Build per-table upsert promises ONLY for dirty tables.
      var writes=[];
      var mlForDb=null; // captured for the diagnostic block on failure
      var loanCollided=[];
      if(dirty.loans){
        // Per-ROW diff against the last-saved snapshot (mirrors marketing_leads).
        // Only loans the admin actually touched are written, so editing loan A
        // can never re-upload — and clobber — a borrower submission sitting on
        // loan B. saveLoansMerged then three-way merges the one loan that DID
        // change with the server copy (borrower writes, the other user's edits).
        var lastLoans=Array.isArray(L.loans)?L.loans:[];
        var lastLoanById=new Map();
        lastLoans.forEach(function(l){if(l&&l.id)lastLoanById.set(l.id,l);});
        var seenLoan={};var changedLoans=[];
        loans.forEach(function(l){
          if(!l||!l.id||seenLoan[l.id])return;
          seenLoan[l.id]=true;
          // Max's save only ever sends files that are his.
          if(ownOnlySave&&ownerOf(l)!==staffKey)return;
          if(lastLoanById.get(l.id)!==l)changedLoans.push(l);
        });
        if(changedLoans.length>0){
          sent.loans=changedLoans;
          writes.push(["loans",saveLoansMerged(changedLoans,lastLoanById,sentLoanFpRef.current).then(function(r){if(r&&r._collided&&r._collided.length)loanCollided=r._collided;return r;})]);
          console.log("[LoanFlow save] loans: "+changedLoans.length+" changed/new upserted (of "+loans.length+" in memory)");
        }
      }
      var settingsMerged=null;var settingsBlocked=false;
      if(dirty.settings){
        // If the settings SELECT failed at load, state still holds the in-code
        // defaults — writing them would replace the real rate sheets / HECM
        // limit / DM campaigns. Only write once we know what the server has
        // ("ok") or that the row doesn't exist yet ("missing"). Even then the
        // write is a three-way merge with a fresh read: only the settings THIS
        // tab changed are written over the server's copy, so a stale tab (or
        // Max changing one thing) can't roll back newer sheets / swap marks.
        var sls=settingsLoadStateRef.current;
        if(sls==="ok"||sls==="missing"){
          settingsSaveInflightRef.current=true;
          writes.push(["settings",saveSettingsMerged(settings,L.settings).then(function(r){if(r&&r._merged)settingsMerged=r._merged;return r;})]);
        } else {
          settingsBlocked=true;
          console.warn("[LoanFlow save] settings NOT written — server copy never loaded this session ("+sls+"). Reload to sync before editing settings.");
        }
      }
      if(dirty.mktgLeads){
        // Per-ROW diff against the last-saved snapshot. The old code
        // re-upserted the entire table on any change — at 45K leads that
        // turned a one-field edit into a multi-minute 45-chunk upload.
        // React state updates replace only the objects that changed, so
        // reference inequality vs the baseline identifies exactly the
        // edited/new rows.
        var lastLeads=Array.isArray(L.mktgLeads)?L.mktgLeads:[];
        var lastById=new Map();
        lastLeads.forEach(function(l){if(l&&l.id)lastById.set(l.id,l);});
        var mlSeen={};var changedRows=[];
        mktgLeads.forEach(function(l){
          if(!l.id||mlSeen[l.id])return;
          mlSeen[l.id]=true;
          if(lastById.get(l.id)!==l)changedRows.push(l);
        });
        // ADDITIVE-ONLY save. This path upserts new/changed rows and NEVER
        // issues blanket deletes.
        //
        // The previous version computed `deletedIds` = every id in the
        // last-saved baseline that was not currently in memory, and DELETEd
        // them. That is catastrophically wrong once the lead table is
        // lazy-loaded (only a subset is ever in memory) and once rows are
        // inserted server-side by /api/intake (never in the browser at all):
        // "not in memory" does NOT mean "the user deleted it." A single
        // routine save could — and did — wipe the entire prospect list down
        // to the handful of rows that happened to be loaded.
        //
        // Explicit, intentional single-lead deletion is now handled directly
        // in deleteLead() with a targeted .delete().eq('id', …). A save can no
        // longer remove a lead as a side effect.
        sent.mktgLeads=changedRows;
        mlForDb=changedRows.map(leadToDb);
        // Each changed lead is three-way merged with a fresh server read
        // (saveLeadsMerged) — the other user's note / DNC / owner tag on the
        // same lead is never reverted by this tab's older copy.
        if(changedRows.length>0){
          writes.push(["marketing_leads",saveLeadsMerged(changedRows,lastById)]);
          console.log("[LoanFlow save] marketing_leads: "+changedRows.length+" changed/new merged + upserted (of "+mktgLeads.length+" in memory)");
        }
      }
      // start_date is a DATE column: one '' (an old cache, a legacy path)
      // fails the whole single-request upsert and every project vanishes on
      // reload — send null for anything that isn't YYYY-MM-DD.
      // Campaigns / portal accounts / A-B tests / touch schedule: only rows
      // that are new or edited HERE are sent (per-row diff vs the baseline),
      // never the whole array — a stale tab re-sending everything revived a
      // portal code another user had revoked and reverted their project edits.
      // Deletes stay explicit (targeted .delete() where they happen).
      if(dirty.campaigns){
        var chCamp=changedRowsVs(campaigns,L.campaigns);sent.campaigns=chCamp;
        if(chCamp.length)writes.push(["campaigns",supabase.from('campaigns').upsert(chCamp.map(function(c){return Object.assign({},c,{start_date:/^\d{4}-\d{2}-\d{2}$/.test(String(c.start_date||""))?c.start_date:null});}))]);
      }
      var expCollided=[];
      if(dirty.expenses){
        // Per-ROW diff, like loans/leads — never the whole array. A stale tab
        // or second device used to re-upsert its entire list on any edit:
        // reverting edits made elsewhere and reviving rows deleted elsewhere.
        // Deletes stay explicit (Expenses → Delete runs a targeted delete).
        // NEW rows are insert-only: if the id is already taken on the server
        // (another device minted the same number) nothing is overwritten —
        // the row is renumbered and saved under the next free id.
        var lastExp=Array.isArray(L.expenses)?L.expenses:[];
        var lastExpById=new Map();
        lastExp.forEach(function(e){if(e&&e.id)lastExpById.set(e.id,e);});
        var seenExp={};var newExp=[];var editExp=[];
        expenses.forEach(function(e){
          if(!e||!e.id||seenExp[e.id])return;
          seenExp[e.id]=true;
          var be=lastExpById.get(e.id);
          if(be===e)return;
          if(be)editExp.push(e);else newExp.push(e);
        });
        sent.expenses=newExp.concat(editExp);
        // Full payload; a missing column (notes / owner until
        // supabase/2026-10-01-two-users.sql is run) is stripped on its own —
        // vendor, recurring and receipt path still save — and the pill warns.
        if(editExp.length)writes.push(["expenses",writeTiered('expenses',editExp.map(expenseToDb),function(rows){return supabase.from('expenses').upsert(rows);},editExp.map(expenseToDbSlim))]);
        if(newExp.length)writes.push(["expenses (new)",insertNewExpenses(newExp,sentExpenseFpRef.current).then(function(r){if(r&&r._collided){expCollided=r._collided;r._collided.forEach(function(id){expForeignIdsRef.current.add(id);});}return r;})]);
        if(sent.expenses.length)console.log("[LoanFlow save] expenses: "+newExp.length+" new, "+editExp.length+" edited (of "+expenses.length+" in memory)");
      }
      if(dirty.borrowerAccts){
        var chBa=changedRowsVs(borrowerAccts,L.borrowerAccts);sent.borrowerAccts=chBa;
        if(chBa.length)writes.push(["borrower_accounts",supabase.from('borrower_accounts').upsert(chBa.map(baToDb))]);
      }
      if(dirty.smsLog){
        // Full payload (delivery/error) first; a missing column is stripped on
        // its own until supabase/2026-10-01-two-users.sql adds them.
        writes.push(["sms_log",writeTiered('sms_log',smsLog.map(smsToDb),function(rows){return supabase.from('sms_log').upsert(rows);},smsLog.map(smsToDbSlim))]);
      }
      if(dirty.abTests){
        var chAb=changedRowsVs(abTests,L.abTests);sent.abTests=chAb;
        if(chAb.length)writes.push(["ab_tests",supabase.from('ab_tests').upsert(chAb)]);
      }
      if(dirty.touchSchedule){
        var chTs=changedRowsVs(touchSchedule,L.touchSchedule);sent.touchSchedule=chTs;
        if(chTs.length)writes.push(["touch_schedule",supabase.from('touch_schedule').upsert(chTs)]);
      }
      if(writes.length===0){
        // Reference changes with no value changes (e.g. server-sourced rows
        // appended by the poll). Advance the baseline for those tables so the
        // diff doesn't re-run on every subsequent render.
        Object.keys(dirty).forEach(function(k){if(k==="settings"&&settingsBlocked)return;if(L[k]===startBase[k])L[k]=current[k];});
        if(settingsBlocked){setSaveStatus({state:"error",failedTables:["settings (not loaded — reload the CRM)"],at:Date.now()});return false;}
        setSaveStatus({state:"idle",failedTables:[],at:null});
        return true;
      }
      console.log("[LoanFlow save] dispatching "+writes.length+" write(s) for changed: "+Object.keys(dirty).join(","));
      try{
        var results=await Promise.all(writes.map(function(w){return w[1].then(function(r){return{name:w[0],result:r};}).catch(function(e){return{name:w[0],result:{error:e}};});}));
        settingsSaveInflightRef.current=false;
        // The session ended while this was in flight — its baseline is gone.
        if(sessionGenRef.current!==sessGen)return false;
        var failures=results.filter(function(r){return r.result&&r.result.error;});
        if(settingsBlocked)failures.push({name:"settings (not loaded — reload the CRM)",result:{error:{message:"settings were never loaded this session"}}});
        if(failures.length){
          console.group("[LoanFlow save] "+failures.length+" of "+results.length+" tables FAILED");
          failures.forEach(function(f){
            var err=f.result.error;
            console.error(
              "  "+f.name+" →",
              err.message||err.msg||String(err),
              err.code?"(code="+err.code+")":"",
              err.details||err.hint?"| "+(err.details||err.hint):""
            );
            // Extra payload diagnostic for marketing_leads upsert
            // failures — code 21000 means duplicate constrained values
            // somewhere in the batch. Print id + a few candidate-key
            // columns so we can spot which column has duplicates.
            if(f.name==="marketing_leads"&&err.code==="21000"){
              try{
                var ids=mlForDb.map(function(r){return r.id;});
                var idSet=new Set(ids);
                var dupIds=ids.filter(function(x,i){return ids.indexOf(x)!==i;});
                console.warn("[diagnostic] mlForDb rows="+mlForDb.length+", unique ids="+idSet.size+", id duplicates="+dupIds.length);
                if(dupIds.length)console.warn("  duplicate id samples:",dupIds.slice(0,5));
                // Look for duplicates in candidate-key columns
                ["email","phone","address","name"].forEach(function(col){
                  var seen={};var dups=0;var sample=null;
                  for(var i=0;i<mlForDb.length;i++){
                    var v=mlForDb[i][col];
                    if(v&&v.length>0){
                      if(seen[v]){if(dups<5&&!sample)sample=v;dups++;}
                      else seen[v]=true;
                    }
                  }
                  if(dups>0)console.warn("  duplicate "+col+" count="+dups+(sample?" — sample: "+JSON.stringify(sample):""));
                });
                window.__loanflow_lastFailedPayload=mlForDb;
                console.warn("  full failed payload available as: window.__loanflow_lastFailedPayload");
              }catch(diagErr){console.warn("  diagnostic failed:",diagErr);}
            }
          });
          console.groupEnd();
          // Max's files refused by the database's row rules (42501) means the
          // 10/2 SQL that gives him his own pipeline hasn't been run yet.
          setSaveStatus({state:"error",failedTables:failures.map(function(f){
            var er=f.result&&f.result.error;
            if(ownOnlySave&&f.name==="loans"&&er&&(er.code==="42501"||/row-level security/i.test(er.message||"")))return"your files (run supabase/2026-10-02-max-own-pipeline.sql)";
            return f.name;
          }),at:Date.now()});
          scheduleRetry(attempt);
          return false;
        }
        console.log("[LoanFlow save] OK — "+results.length+" write(s) at "+new Date().toLocaleTimeString());
        // Advance the baseline for exactly what was sent. Next render's
        // dirty-check compares against it so unchanged tables are skipped.
        var Lnow=lastSavedRef.current;
        Object.keys(dirty).forEach(function(k){
          if(k==="settings"){
            if(settingsBlocked)return;
            if(settingsMerged&&!seq(settingsMerged,current.settings)){
              Lnow.settings=settingsMerged;
              // Catch this tab up to what is now on the server (other tabs'
              // sheets / swap marks). An edit made while this save was in
              // flight is rebased on top, not lost.
              var sentS=current.settings,mergedS=settingsMerged;
              setSettings(function(prev){return prev===sentS?mergedS:mergeSettings3(prev,sentS,mergedS);});
            } else Lnow.settings=current.settings;
            return;
          }
          var sentRows=sent[k]||[];
          var hold=(k==="expenses"&&expCollided.length)?new Set(expCollided):(k==="loans"&&loanCollided.length)?new Set(loanCollided):null;
          if(hold)sentRows=sentRows.filter(function(e){return!hold.has(e.id);});
          if(Lnow[k]===startBase[k]&&!hold){Lnow[k]=current[k];return;}
          if(k==="loans"||k==="mktgLeads"||k==="expenses"||k==="campaigns"||k==="borrowerAccts"||k==="abTests"||k==="touchSchedule"){Lnow[k]=patchBaseline(Lnow[k],sentRows);return;}
          if(Lnow[k]===startBase[k])Lnow[k]=current[k];
        });
        if(expCollided.length)remintExpenseIds(expCollided);
        if(loanCollided.length)remintLoanIds(loanCollided);
        // Written, but some columns don't exist yet → say so, loudly.
        var missing=[];
        results.forEach(function(x){if(x.result&&Array.isArray(x.result._missing))x.result._missing.forEach(function(c){if(missing.indexOf(c)<0)missing.push(c);});});
        if(missing.length){
          setSchemaWarn(function(p){var n=p.slice();missing.forEach(function(c){if(n.indexOf(c)<0)n.push(c);});return n.length===p.length?p:n;});
          setSaveStatus({state:"partial",failedTables:missing,at:Date.now()});
          return true;
        }
        setSaveStatus({state:"saved",failedTables:[],at:Date.now()});
        // Auto-clear the "saved" badge after a few seconds so it doesn't
        // sit permanently — just a confirmation flash.
        setTimeout(function(){setSaveStatus(function(s){return s.state==="saved"?{state:"idle",failedTables:[],at:null}:s;});},4000);
        return true;
      }catch(e){
        settingsSaveInflightRef.current=false;
        console.error("[LoanFlow save] unexpected exception",e);
        if(sessionGenRef.current!==sessGen)return false;
        setSaveStatus({state:"error",failedTables:["unknown"],at:Date.now()});
        scheduleRetry(attempt);
        return false;
      }
    };
    var runSave=async function(attempt){
      // Never write after the session that scheduled this has ended.
      if(sessionGenRef.current!==sessGen)return false;
      if(pendingSaveRef.current===runSave)pendingSaveRef.current=null;
      var p=doSave(attempt||0);
      saveInflightRef.current=p;
      var inflight=saveInflightSetRef.current;inflight.add(p);
      var ok=false;
      try{ok=await p;}catch(e){ok=false;}
      inflight.delete(p);
      if(saveInflightRef.current===p)saveInflightRef.current=null;
      return ok;
    };
    // One automatic retry after a failure (network blip, laptop wake, token
    // refresh mid-request). Skipped if a newer save has been scheduled since —
    // that newer save carries the still-dirty tables anyway, because the
    // baseline is only advanced on success.
    var scheduleRetry=function(attempt){
      if(attempt>=1)return;
      setTimeout(function(){
        if(saveGenRef.current!==gen||sessionGenRef.current!==sessGen)return;
        console.warn("[LoanFlow save] retrying failed save (attempt "+(attempt+2)+")");
        runSave(attempt+1);
      },6000);
    };
    latestRunSaveRef.current=runSave;
    pendingSaveRef.current=runSave;
    saveTimer.current=setTimeout(function(){runSave(0);},2000);
    return function(){
      clearTimeout(saveTimer.current);clearTimeout(lsSaveTimer.current);
      if(pendingSaveRef.current===runSave)pendingSaveRef.current=null;
    };
  },[loans,mktgLeads,campaigns,expenses,borrowerAccts,smsLog,settings,abTests,touchSchedule,mailTemplates,sbLoaded,user,limited,staffPrefs]);// eslint-disable-line react-hooks/exhaustive-deps

  // Hardcoded target for all inquiry notifications — never driven by settings
  // so a misconfiguration can't silently route leads away from Reece's phone.
  const REECE_PHONE="+13028938949";

  const sendSMS=function(toNum,msg,type){
    var ts=new Date();
    // All remaining sendSMS() calls are admin-directed inquiry notifications;
    // force the destination to REECE_PHONE regardless of what the caller passed.
    var dest=REECE_PHONE;
    var entry={id:"SMS-"+Date.now()+"-"+Math.random().toString(36).slice(2,6),to:dest,message:msg,type:type,timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),delivery:"pending"};
    setSmsLog(function(p){return[entry].concat(p);});
    // Live send via RingCentral if connected — otherwise entry stays as "pending" in the log.
    if(RC.isAuthed()&&settings.rcEnabled){
      RC.sendSMS(settings,dest,msg,settings.rcSmsFrom||"").then(function(res){
        setSmsLog(function(p){return p.map(function(e){return e.id===entry.id?Object.assign({},e,{delivery:res.ok?"sent":"failed",error:res.error||null}):e;});});
        if(!res.ok)console.warn("RC SMS send failed:",res.error,res.detail);
      }).catch(function(e){console.warn("RC SMS exception:",e);});
    }
    return entry;
  };

  // ── RingCentral: inbound call state + OAuth callback handling ──
  const [inboundCall,setInboundCall]=useState(null); // { from, fromName, match, sessionId, ts }
  const [rcAuthed,setRcAuthed]=useState(RC.isAuthed());
  const [rcStatus,setRcStatus]=useState("idle"); // idle | connecting | connected | error
  // Handle ?code= on mount (once)
  useEffect(()=>{
    (async()=>{
      try{
        var qs=new URLSearchParams(window.location.search);
        if(qs.get("code")){
          await RC.handleOAuthCallbackIfPresent(settings);
          setRcAuthed(RC.isAuthed());
        }
      }catch(e){console.warn("RC callback handling failed",e);}
    })();
  },[]);// eslint-disable-line react-hooks/exhaustive-deps

  // ── Deep link: /crmadmin?loan=<id> opens that loan's detail directly ──
  // Admin alert emails (e.g. a borrower statement submission) link here so the
  // notification jumps straight to the loan — and its Borrower Documents card
  // where the statement's View button lives — instead of a manual hunt. Fires
  // once, after the admin is logged in and loans have loaded; if the loan isn't
  // in memory yet it waits for the next load rather than consuming the param.
  const deepLinkRef=useRef(false);
  useEffect(function(){
    if(deepLinkRef.current)return;
    if(user!=="admin"||!sbLoaded||limitedRef.current)return;
    var id;
    try{id=new URLSearchParams(window.location.search).get("loan");}catch(e){return;}
    if(!id)return;
    var loan=loans.find(function(l){return String(l.id)===String(id);});
    if(!loan)return;
    deepLinkRef.current=true;
    setSel(loan);setNav("loanDetail");
    try{
      var u=new URL(window.location.href);
      u.searchParams.delete("loan");
      window.history.replaceState({},document.title,u.pathname+u.search+u.hash);
    }catch(e){}
  },[user,sbLoaded,loans]);

  // Subscribe to inbound call events while admin is logged in and RC is enabled+authed.
  // RingCentral is Reece's line — never started for Max's login.
  const loansRef=useRef(loans);useEffect(()=>{loansRef.current=loans;},[loans]);
  const leadsRef=useRef(mktgLeads);useEffect(()=>{leadsRef.current=mktgLeads;},[mktgLeads]);
  useEffect(()=>{
    if(user!=="admin")return;
    if(staff!=="reece"||!staffAccess(staff).newCall)return;
    if(!settings.rcEnabled)return;
    if(!rcAuthed)return;
    var disposed=false;var disposeFn=null;
    setRcStatus("connecting");
    RC.subscribeInboundCalls(settings,function(evt){
      var fromLast10=RC.last10(evt.from);
      var match=null;
      if(fromLast10){
        var loan=loansRef.current.find(function(l){return RC.last10(l.ph)===fromLast10;});
        if(loan){match={kind:"loan",id:loan.id,name:loan.b,stage:loan.stage};}
        else{
          var lead=leadsRef.current.find(function(x){return RC.last10(x.phone)===fromLast10;});
          if(lead){match={kind:"lead",id:lead.id,name:lead.name,status:lead.status};}
        }
      }
      // Log BEFORE showing the toast. The toast is transient; this is the
      // record. A call that rings while he's away from the desk used to leave
      // no trace at all once the toast timed out.
      var now=new Date();
      pushCallLog({from:evt.from,fromName:evt.fromName||"",epoch:now.getTime(),iso:now.toISOString(),
        matchKind:match?match.kind:null,matchId:match?match.id:null,matchName:match?match.name:null,handled:false});
      setInboundCall({from:evt.from,fromName:evt.fromName,match:match,sessionId:evt.sessionId,
        epoch:now.getTime(),ts:now.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})});
    },function(s){if(!disposed)setRcStatus(s==="connected"?"connected":s==="reconnecting"?"connecting":"error");},
    // A call ended: Calls to return re-reads the call log ~45 s later if it is on screen.
    function(){if(!disposed)noteCallEnded();}).then(function(d){if(disposed){try{d&&d();}catch(e){}}else{disposeFn=d;setRcStatus("connected");}}).catch(function(e){console.warn("RC subscribe failed",e);setRcStatus("error");});
    return function(){disposed=true;if(disposeFn)try{disposeFn();}catch(e){}setRcStatus("idle");};
  },[user,staff,settings.rcEnabled,rcAuthed,settings.rcClientId,settings.rcServerUrl]);

  // ── Admin login: Supabase Auth is the primary gate ──
  // Historical note: an earlier version checked `settings.adminPasswordHash`
  // from the DB first. That broke once RLS blocked anon from reading the
  // settings table — the client fell back to the hardcoded default hash, and
  // the only password that worked was the factory default. The fix is to
  // trust Supabase Auth as the source of truth when it's configured. We keep
  // the legacy hash path as a fallback for local dev without Supabase.
  const isLockedOut=()=>Date.now()<lockoutUntil;
  const adminLogin=async ()=>{
    if(isLockedOut()){setAdminErr(true);return;}
    if(!adminPw.trim()){setAdminErr(true);return;}

    // Which staff login this form is for: /max → Max, otherwise Reece.
    const who=loginAs==="max"?"max":"reece";

    // ── Primary path: Supabase Auth is configured ──
    if(isSupabaseReady()){
      const {data:signIn,error}=await supabase.auth.signInWithPassword({email:who==="max"?MAX_EMAIL:ADMIN_EMAIL,password:adminPw.trim()});
      if(error){
        // Wrong password OR admin user doesn't exist. Either way: deny.
        console.warn("Supabase admin sign-in failed:",error.message);
        const next=loginAttempts+1;
        setLoginAttempts(next);
        if(next>=MAX_ATTEMPTS){setLockoutUntil(Date.now()+LOCKOUT_DURATION);setLoginAttempts(0);}
        setAdminErr(true);
        return;
      }
      // Practice mode / LO split ride on the login itself (user_metadata),
      // fresh from this sign-in — known before the first screen paints.
      if(signIn&&signIn.user)primeStaffPrefs(who,signIn.user);else loadStaffPrefs(who,true);
      setStaff(who);setUser("admin");setAdminErr(false);setAdminPw("");setLoginAttempts(0);setLockoutUntil(0);
      return;
    }

    // ── Fallback path: no Supabase — check the app-level hash directly ──
    // (Local dev only. /max accepts the same local password and signs in as
    // Max so the two-user flows can be tried without Supabase.)
    const stored=settings.adminPasswordHash;
    let match=false;
    if(isLegacyPassword(stored)){
      match=(adminPw.trim()===stored);
    } else {
      const h=await hashPassword(adminPw.trim());
      match=(h===stored);
    }
    if(match){
      setStaff(who);setUser("admin");setAdminErr(false);setAdminPw("");setLoginAttempts(0);setLockoutUntil(0);
    }
    else{
      const next=loginAttempts+1;
      setLoginAttempts(next);
      if(next>=MAX_ATTEMPTS){setLockoutUntil(Date.now()+LOCKOUT_DURATION);setLoginAttempts(0);}
      setAdminErr(true);
    }
  };

  // Secure random token generator — used for borrower portal access codes
  const genToken=function(){
    var chars="abcdefghjkmnpqrstuvwxyz23456789"; // no ambiguous chars (0/O, 1/l/I)
    var result="";
    var arr=new Uint8Array(8);
    window.crypto.getRandomValues(arr);
    arr.forEach(function(b){result+=chars[b%chars.length];});
    return result;
  };

  // Shared logout — always clear the Supabase session so the anon key takes
  // over again before we blank the in-app state.
  const doLogout=async()=>{
    // An edit made in the last 2s (or one whose save failed) only exists in
    // this tab. Push it out before the session (and the right to write) ends.
    if(user==="admin")await flushCallConsole();
    if(user==="admin"&&isSupabaseReady()){
      const ok=await flushPendingSave();
      if(!ok&&!window.confirm("Your latest change has NOT saved to the cloud (check your connection).\n\nLog out anyway and lose it?"))return;
    }
    if(isSupabaseReady()){try{await supabase.auth.signOut();}catch(e){console.warn("signOut failed",e);}}
    // Clear any borrower-portal JWT alongside the Supabase session so one
    // role can't leave residual credentials behind for the next.
    try{BorrowerAPI.clearToken();}catch(e){}
    // Wipe in-memory tables that require authenticated reads. This prevents
    // an ex-admin's expenses/leads/SMS log from lingering in React state
    // after they sign out. The next authenticated load will repopulate.
    // (Local dev mode has no server to reload from — the data only lives in
    // this tab/localStorage — so keep it for the next login, e.g. trying /max.)
    if(isSupabaseReady()){
      setLoans([]);setMktgLeads([]);setCampaigns([]);setExpenses([]);
      setBorrowerAccts([]);setSmsLog([]);setAbTests([]);setTouchSchedule([]);
    }
    // The [user] effect resets the rest of the session (lead flags, save
    // baseline, poll state, staff) so the next login loads cleanly.
    setUser(null);setAdminPw("");setBorrowerPw("");
  };

  // ── PUBLIC "/info" — static explainer page ────────────────────────────
  // Served path-based like ADMIN_PATH (the Vercel rewrite sends every path
  // to index.html). Fully public and static: no state, no auth, no forms.
  if(typeof window!=="undefined" && window.location.pathname==="/info"){
    return <InfoPage/>;
  }
  // PUBLIC "/second" — keep-your-first-mortgage explainer (HomeSafe Second).
  if(typeof window!=="undefined" && window.location.pathname==="/second"){
    return <SecondPage/>;
  }

  if(!user) return <LoginScreen
    onAdmin={adminLogin} adminPw={adminPw} setAdminPw={setAdminPw} adminErr={adminErr}
    lockoutUntil={lockoutUntil} loginAttempts={loginAttempts} maxAttempts={MAX_ATTEMPTS}
    borrowerPw={borrowerPw} setBorrowerPw={setBorrowerPw} borrowerErr={borrowerErr} setBorrowerErr={setBorrowerErr}
    setUser={setUser} loans={loans} setLoans={setLoans}
    sendSMS={sendSMS} adminPhone={settings.adminPhone}
    adminGateOpen={adminGateOpen} loginAs={loginAs}/>;
  if(user&&user.type==="borrower") return <BorrowerPortal loans={loans} setLoans={setLoans} borrowerAccts={borrowerAccts} setBorrowerAccts={setBorrowerAccts} userId={user} onLogout={doLogout} sendSMS={sendSMS} adminPhone={settings.adminPhone}/>;

  // A limited login (Max) gets four screens and lands on the Calculator. (His
  // own Pipeline — 10/2 — only appears if access.pipeline is switched back on.)
  const navItems=limited?[
    {id:"calculator",l:"Calculator",i:Ic.calc},
    ...(access.pipeline?[{id:"pipeline",l:"Pipeline",i:Ic.pipe}]:[]),
    {id:"expenses",l:"Expenses",i:Ic.receipt},
    {id:"library",l:"Lender Library",i:Ic.book},
    {id:"settings",l:"Settings",i:Ic.lock},
  ]:[
    {id:"dashboard",l:"Command Center",i:Ic.dash},
    {id:"pipeline",l:"Pipeline",i:Ic.pipe},
    {id:"inquiry",l:"Inquiry",i:Ic.intake},
    {id:"marketing",l:"Marketing",i:Ic.mktg},
    {id:"outbound",l:"Outbound",i:Ic.phone},
    {id:"letters",l:"Handwritten Letters",i:Ic.mail},
    {id:"expenses",l:"Expenses",i:Ic.receipt},
    {id:"library",l:"Lender Library",i:Ic.book},
    {id:"settings",l:"Settings",i:Ic.lock},
  ];
  // The screen actually shown: a nav this login can't use (left over from the
  // other login in this tab, or a link) falls back to its first screen. Loan
  // Detail comes with the pipeline; Max's "inquiry" is only ever re-pricing
  // one of his own files (Loan Detail → Calculate), never the lead screen.
  const selOwnFile=ownOnly&&sel?myLoans.find(function(l){return l.id===sel.id;})||null:null;
  const view=(navItems.some(function(x){return x.id===nav;})
    ||(access.pipeline&&nav==="loanDetail")
    ||(ownOnly&&nav==="inquiry"&&!!selOwnFile))?nav:navItems[0].id;
  const currentNav=navItems.find(function(x){return x.id===view;})
    ||(ownOnly&&(view==="loanDetail"||view==="inquiry")?navItems.find(function(x){return x.id==="pipeline";}):null)
    ||navItems[0];
  const goNav=function(id){setNav(id);setSel(null);setTo(null);setNavOpen(false);setFuFocus(0);};
  // The bell: straight to the Command Center's Follow-ups panel.
  const openFollowUps=function(){setNav("dashboard");setSel(null);setTo(null);setNavOpen(false);setFuFocus(function(n){return n+1;});};
  // "Lead assignment & Max on files" (src/features.js): Reece's switch in
  // Settings, OFF by default — owner chips, "Whose files", reassign menus.
  // Max's own screens never change with it (always as they were).
  const ownershipUi=limited||ownershipUiOn(settings);

  return(<OwnershipUiProvider value={ownershipUi}><div style={{minHeight:"100vh",width:"100%",background:C.bg,color:C.text,fontFamily:"'Mulish',system-ui,sans-serif"}}>
    <FontLoader/>
    <style>{`@keyframes lf-nav-spin{to{transform:rotate(360deg)}}`}</style>

    {/* ── Top bar — hamburger, brand, current view ── */}
    <div style={{position:"sticky",top:0,zIndex:90,display:"flex",alignItems:"center",gap:14,padding:"14px 24px",background:C.glass,backdropFilter:"blur(14px)",WebkitBackdropFilter:"blur(14px)",borderBottom:"1px solid "+C.glassBorder}}>
      <button onClick={()=>setNavOpen(true)} title="Menu (⌘K)" style={{width:40,height:40,borderRadius:10,border:"1px solid "+C.border,background:C.card,color:C.text,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" viewBox="0 0 24 24"><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></svg>
      </button>
      <div style={{display:"flex",alignItems:"center",gap:14,flex:1,minWidth:0}}>
        <Wordmark tone="navy" size={15}/>
        <div style={{width:1,height:22,background:C.border}}/>
        <div style={{display:"flex",alignItems:"center",gap:8,color:C.textMuted,minWidth:0}}>
          <span style={{color:C.accentLight,opacity:.8,flexShrink:0}}>{currentNav.i}</span>
          <span style={{fontSize:13,fontWeight:600,color:C.text,letterSpacing:.2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{currentNav.l}</span>
        </div>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
        {/* ── New Call — the one control that is on every screen ──
            Deliberately the loudest thing in the bar. Every inbound call
            starts here: find them, see everything on file, price it, and
            land the call somewhere real. */}
        {access.newCall&&<button onClick={function(){startCall(null);}} title="Start a call (⌘/Ctrl + Shift + K)"
          style={{display:"inline-flex",alignItems:"center",gap:8,padding:"9px 16px",borderRadius:22,border:"none",
            background:"linear-gradient(135deg,"+C.clay+","+C.clayDeep+")",color:"#fff",fontSize:13,fontWeight:800,
            cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",boxShadow:"0 2px 10px rgba(201,123,92,0.38)",whiteSpace:"nowrap"}}>
          {Ic.phone} New Call
        </button>}
        {/* Follow-ups bell (Reece) — overdue + due-today follow-ups and
            appointments; opens the Command Center's Follow-ups panel. */}
        {access.dashboard&&<button type="button" data-testid="followup-bell" onClick={openFollowUps}
          aria-label={fuDueCount>0?fuDueCount+" follow-up"+(fuDueCount===1?"":"s")+" overdue or due today":"Follow-ups — nothing due today"}
          title={fuDueCount>0?fuDueCount+" follow-up"+(fuDueCount===1?"":"s")+" / appointment"+(fuDueCount===1?"":"s")+" overdue or due today — open Follow-ups":"Follow-ups — nothing overdue or due today"}
          style={{position:"relative",width:38,height:38,borderRadius:10,border:"1px solid "+(fuDueCount>0?C.orange+"70":C.border),background:fuDueCount>0?C.orangeBg:C.card,color:fuDueCount>0?C.orange:C.textMuted,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          {fuDueCount>0&&<span data-testid="followup-badge" style={{position:"absolute",top:-6,right:-6,minWidth:18,height:18,padding:"0 5px",borderRadius:9,background:C.red,color:"#fff",fontSize:10.5,fontWeight:800,lineHeight:"18px",textAlign:"center",fontFamily:"'Mulish',system-ui,sans-serif",boxSizing:"border-box"}}>{fuDueCount>99?"99+":fuDueCount}</span>}
        </button>}
        {practice&&<span title="Practice mode is on — nothing is being saved. Turn it off in Settings."
          style={{fontSize:11,fontWeight:800,color:C.orange,background:C.orangeBg,border:"1px solid "+C.orange+"55",borderRadius:20,padding:"4px 10px",whiteSpace:"nowrap",fontFamily:"'Mulish',system-ui,sans-serif"}}>🧪 Practice — not saving</span>}
        {/* Save status pill — visible at a glance so silent save failures
            can never again eat hours of work */}
        {(() => {
          let s = saveStatus.state;
          // A column the database is missing means those fields are NOT being
          // stored on any save — never let the pill settle on "All changes
          // saved" while that is true.
          const missing = (s === "partial" && saveStatus.failedTables.length) ? saveStatus.failedTables : schemaWarn;
          if ((s === "idle" || s === "saved") && schemaWarn.length) s = "partial";
          if (s === "idle") return null;
          const cfg = {
            pending: { label: "Unsaved changes…", color: C.textDim, bg: C.surfaceAlt, border: C.border, icon: "●" },
            saving:  { label: "Saving…",          color: C.accentLight, bg: C.accentGlow, border: C.accent + "40", icon: "⟳" },
            saved:   { label: "✓ All changes saved", color: C.green, bg: C.greenBg, border: C.green + "40", icon: "✓" },
            partial: { label: "⚠ Not all saved — run supabase/2026-10-01-two-users.sql (" + (missing.slice(0,2).join(", ")||"missing columns") + ")", color: C.orange||C.red, bg: C.orangeBg||C.redBg, border: (C.orange||C.red) + "60", icon: "⚠" },
            error:   { label: "⚠ Save FAILED — " + (saveStatus.failedTables.slice(0,2).join(", ")||"see console"), color: C.red, bg: C.redBg, border: C.red + "60", icon: "⚠" },
          }[s] || null;
          if (!cfg) return null;
          const tip = s === "error" ? "Click to see full error in the browser console (F12)"
            : s === "partial" ? "Saved — but the database is missing these columns, so those fields are NOT being stored: " + missing.join(", ") + ". Run supabase/2026-10-01-two-users.sql in the Supabase SQL Editor, then reload the CRM."
            : "";
          return (
            <span title={tip}
              style={{fontSize:11,fontWeight:600,color:cfg.color,background:cfg.bg,border:"1px solid "+cfg.border,borderRadius:20,padding:"4px 12px",whiteSpace:"nowrap",maxWidth:460,overflow:"hidden",textOverflow:"ellipsis",cursor:(s==="error"||s==="partial")?"help":"default"}}>
              {cfg.label}
            </span>
          );
        })()}
        {/* Lender Library answer still generating while he's elsewhere */}
        {libraryBusy&&view!=="library"&&<button onClick={function(){goNav("library");}} title="A Lender Library answer is still being written — click to see it"
          style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:11,fontWeight:700,color:C.navy,background:C.cream,border:"1px solid "+C.border,borderRadius:20,padding:"4px 10px",whiteSpace:"nowrap",cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif"}}>
          <span style={{width:9,height:9,borderRadius:"50%",border:"2px solid "+C.clay,borderTopColor:"transparent",display:"inline-block",animation:"lf-nav-spin .8s linear infinite"}}/>Library answering…
        </button>}
        {/* Who is signed in (Reece sees everything; Max his own files) */}
        <span title={"Signed in as "+STAFF[staffKey].name} style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:11,fontWeight:700,color:C.textMuted,padding:"3px 10px 3px 3px",border:"1px solid "+C.border,borderRadius:20,whiteSpace:"nowrap",fontFamily:"'Mulish',system-ui,sans-serif"}}>
          <span style={{width:20,height:20,borderRadius:"50%",background:staffKey==="max"?C.clay:C.navy,color:"#fff",fontSize:10,fontWeight:800,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{STAFF[staffKey].short}</span>
          Signed in as {STAFF[staffKey].name}
        </span>
        <span style={{fontSize:10,color:C.textDim,padding:"4px 8px",border:"1px solid "+C.border,borderRadius:6,fontFamily:"'Mulish',system-ui,sans-serif"}}>⌘K</span>
        <Btn small onClick={doLogout} style={{color:C.textDim}}>Logout</Btn>
      </div>
    </div>

    {/* ── Slide-in drawer nav ── */}
    {navOpen&&<div onClick={()=>setNavOpen(false)} style={{position:"fixed",inset:0,zIndex:200,background:"rgba(6,26,48,0.55)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",animation:"fadeIn 0.15s ease"}}>
      <nav onClick={e=>e.stopPropagation()} style={{width:280,height:"100%",background:C.navyDeep,color:C.cream,borderRight:"1px solid rgba(242,239,231,0.08)",display:"flex",flexDirection:"column",padding:"18px 0",boxShadow:"2px 0 40px rgba(6,26,48,0.45)",animation:"slideIn 0.2s cubic-bezier(.2,.8,.2,1)"}}>
        <div style={{padding:"0 20px 16px",borderBottom:"1px solid rgba(242,239,231,0.08)",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
          <BrandMark dark/>
          <button onClick={()=>setNavOpen(false)} style={{width:30,height:30,borderRadius:8,border:"1px solid rgba(242,239,231,0.18)",background:"transparent",color:"rgba(242,239,231,0.75)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
        </div>
        <div style={{flex:1,padding:"12px 12px",display:"flex",flexDirection:"column",gap:2,overflowY:"auto"}}>
          {access.newCall&&<button onClick={function(){setNavOpen(false);startCall(null);}} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:8,border:"none",background:C.clay,color:"#fff",fontSize:13.5,fontWeight:800,cursor:"pointer",textAlign:"left",fontFamily:"'Mulish',system-ui,sans-serif",marginBottom:8}}>
            <span style={{display:"flex"}}>{Ic.phone}</span>New Call
          </button>}
          {navItems.map(x=>{var active=view===x.id;var spin=x.id==="library"&&libraryBusy;return<button key={x.id} onClick={()=>goNav(x.id)} title={spin?"An answer is still being written":undefined} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:8,border:"none",borderLeft:"3px solid "+(active?C.clay:"transparent"),background:active?C.navy2:"transparent",color:active?C.cream:"rgba(242,239,231,0.72)",fontSize:13.5,fontWeight:active?700:500,cursor:"pointer",textAlign:"left",fontFamily:"'Mulish',system-ui,sans-serif",transition:"background .15s, color .15s"}} onMouseEnter={e=>{if(!active)e.currentTarget.style.background="rgba(242,239,231,0.06)";}} onMouseLeave={e=>{if(!active)e.currentTarget.style.background="transparent";}}><span style={{opacity:active?1:.85,display:"flex"}}>{x.i}</span>{x.l}{spin&&<span aria-label="answer in progress" style={{marginLeft:"auto",width:10,height:10,borderRadius:"50%",border:"2px solid "+C.clay,borderTopColor:"transparent",display:"inline-block",animation:"lf-nav-spin .8s linear infinite"}}/>}</button>;})}
        </div>
        <div style={{padding:"10px 16px 0",fontSize:11.5,color:"rgba(242,239,231,0.75)",textAlign:"center",fontWeight:600}}>Signed in as {STAFF[staffKey].name}</div>
        <div style={{padding:"12px 16px",borderTop:"1px solid rgba(242,239,231,0.08)",fontSize:10,color:"rgba(242,239,231,0.5)",textAlign:"center"}}>
          Press <span style={{color:"rgba(242,239,231,0.8)"}}>⌘K</span> to toggle · <span style={{color:"rgba(242,239,231,0.8)"}}>Esc</span> to close
        </div>
      </nav>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes slideIn{from{transform:translateX(-100%)}to{transform:translateX(0)}}`}</style>
    </div>}

    {access.dashboard&&<UrgentAlertBanner loans={loans} mktgLeads={mktgLeads} setNav={setNav} setSel={setSel} setMktgLeadFocus={setLeadFocus}/>}

    <main style={{maxWidth:1400,margin:"0 auto",padding:"32px 40px 60px"}}>
      {/* Practice mode — on every screen while it's on */}
      {practice&&<PracticeBanner detail={view==="calculator"&&!access.pipeline?PRACTICE_CALC_DETAIL:PRACTICE_DETAIL[view]}/>}
      {/* First Supabase load in flight — show a calm skeleton instead of empty views */}
      {isSupabaseReady()&&!sbLoaded&&<LoadingSkeleton/>}
      {(!isSupabaseReady()||sbLoaded)&&<div>
      {/* The calculator on its own (Max's landing screen): no files, no
          Pitch. Only with his own pipeline (dormant since 10/2) would it get
          his files — and only his — so a Pitch could make or update one of
          them; never a lead. */}
      {view==="calculator"&&<Calc standalone practice={practice} staff={staffKey} loans={ownOnly?myLoans:NO_ROWS} setLoans={ownOnly?setMyLoans:noop} mktgLeads={NO_ROWS} setNav={ownOnly?setNav:noop} hecmLimit={settings.hecmLimit} rateSheets={settings.rateSheets} setRateSheets={noop}/>}
      {view==="dashboard"&&<Dashboard staff={staffKey} focusFollowUps={fuFocus} registerFlush={regFlush} loans={loans} setLoans={setLoans} expenses={myExpenses} campaigns={campaigns} mktgLeads={mktgLeads} setMktgLeads={setMktgLeads} leadCounts={leadCounts} leadsReady={leadsReady} setNav={setNav} setSel={setSel} setMktgLeadFocus={setLeadFocus} onStartCall={startCall}
        settings={settings} rcAuthed={rcAuthed} adoptLeads={adoptLeads} allLeadsLoaded={allLeadsLoaded} practice={practice}/>}
      {/* Max's Loan Detail: his files only, and nothing that reaches the
          borrower portal or a mail-list lead (LoanDetail hides those for
          access.ownFilesOnly; these props keep them unwired as well). */}
      {view==="loanDetail"&&<LoanDetail staff={staffKey} registerFlush={regFlush} loans={loansForViews} setLoans={setLoansForViews} sel={sel} setSel={setSel} setNav={setNav} borrowerAccts={ownOnly?NO_ROWS:borrowerAccts} setBorrowerAccts={ownOnly?noop:setBorrowerAccts} sendSMS={ownOnly?noop:sendSMS} genToken={genToken} dmCampaigns={settings.dmCampaigns} mktgLeads={ownOnly?NO_ROWS:mktgLeads} setMktgLeads={ownOnly?null:setMktgLeads} adoptLeads={ownOnly?null:adoptLeads}/>}
      {view==="pipeline"&&<Pipeline staff={staffKey} loans={loansForViews} setLoans={setLoansForViews} go={l=>{setSel(l);setNav("loanDetail");}} goCalc={l=>{setSel(l);setNav("inquiry");}} sendSMS={ownOnly?noop:sendSMS} onOpenAutomations={ownOnly?null:()=>setAutoPanelOpen(true)} onStartCall={access.newCall?startCall:null} mktgLeads={ownOnly?NO_ROWS:mktgLeads} rateSheets={settings.rateSheets}/>}
      {view==="inquiry"&&!ownOnly&&<Inquiry staff={staffKey} loans={loans} setLoans={setLoans} mktgLeads={mktgLeads} dmCampaigns={settings.dmCampaigns} setNav={setNav} sel={sel} setSel={setSel} onOpenFile={openPitchedFile} hecmLimit={settings.hecmLimit} rateSheets={settings.rateSheets} setRateSheets={rs=>updateSettings({rateSheets:rs})}/>}
      {/* Max: Loan Detail → Calculate re-prices that one file of his — the
          calculator opened on it, Pitch updating it (never the lead screen). */}
      {view==="inquiry"&&ownOnly&&selOwnFile&&<div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
          <Btn small onClick={function(){setNav("loanDetail");}} style={{color:C.textDim}}>← Back to {selOwnFile.id}</Btn>
          <span style={{fontSize:12,color:C.textDim}}>Re-pricing for <span style={{color:C.accentLight,fontWeight:600}}>{selOwnFile.b||"(unnamed)"}</span> · {selOwnFile.id}</span>
        </div>
        <Calc key={"file-"+selOwnFile.id} practice={practice} staff={staffKey} loans={myLoans} setLoans={setMyLoans} mktgLeads={NO_ROWS} setNav={setNav} hecmLimit={settings.hecmLimit} rateSheets={settings.rateSheets} setRateSheets={noop} initialLead={selOwnFile}/>
      </div>}
      {view==="marketing"&&<Marketing staff={staffKey} mktgLeads={mktgLeads} leadsReady={leadsReady} allLeadsLoaded={allLeadsLoaded} loadAllLeads={loadAllLeads} leadCounts={leadCounts} leadLoadProgress={leadLoadProgress} setMktgLeads={setMktgLeads} campaigns={campaigns} setCampaigns={setCampaigns} loans={loans} setLoans={setLoans} dmCampaigns={settings.dmCampaigns} abTests={abTests} setAbTests={setAbTests} touchSchedule={touchSchedule} setTouchSchedule={setTouchSchedule} mailTemplates={mailTemplates} setMailTemplates={setMailTemplates} setNav={setNav} setSel={setSel} leadFocus={leadFocus} clearLeadFocus={()=>setLeadFocus(null)} onStartCall={startCall} adoptLeads={adoptLeads} practice={practice} selfPhones={[settings.adminPhone,settings.rcSmsFrom]}/>}
      {view==="letters"&&<Letters staff={staffKey} loans={loans} mktgLeads={mktgLeads} adoptLeads={adoptLeads} onStartCall={startCall}/>}
      {view==="outbound"&&<Outbound staff={staffKey} registerFlush={regFlush} mktgLeads={mktgLeads} leadsReady={leadsReady} adoptLeads={adoptLeads} onStartCall={startCall} hecmLimit={settings.hecmLimit} loans={loans} setSel={setSel} setNav={setNav} setMktgLeads={setMktgLeads}/>}
      {view==="expenses"&&<Expenses staff={staffKey} settings={settings} expenses={myExpenses} setExpenses={setMyExpenses} loans={limited?NO_ROWS:loans} loSplit={settings.loSplit} mktgLeads={limited?NO_ROWS:mktgLeads} leadsReady={leadsReady}
        expensesLoaded={!isSupabaseReady()||expensesLoad==="ok"} readServerExpenses={readServerExpenses} beforeServerDelete={beforeExpenseDelete}/>}
      {/* Mounted from the first visit on and only HIDDEN on other screens, so
          an upload or a half-filled form survives working elsewhere. (An
          answer streams in the chat store either way; the nav item and the
          top bar show a spinner until it finishes.) */}
      {/* key: a different login — or Practice mode flipping — starts it fresh
          (the chat store restarts too, src/libraryChat.js). */}
      {(libOpened||view==="library")&&<div style={{display:view==="library"?"block":"none"}}><LenderLibrary key={staffKey+(practice?":practice":"")} staff={staffKey} visible={view==="library"} covered={callOpen} readOnly={!access.uploadLibrary}/></div>}
      {view==="settings"&&<Settings staff={staffKey} onBeforePractice={flushPendingSave} allLeadsLoaded={allLeadsLoaded} loadAllLeads={loadAllLeads} settings={settings} updateSettings={updateSettings} smsLog={smsLog} setSmsLog={setSmsLog} loans={loans} setLoans={setLoans} borrowerAccts={borrowerAccts} setBorrowerAccts={setBorrowerAccts} mktgLeads={mktgLeads} setMktgLeads={setMktgLeads} leadsReady={leadsReady} campaigns={campaigns} setCampaigns={setCampaigns} expenses={myExpenses} setExpenses={setMyExpenses} rcAuthed={rcAuthed} setRcAuthed={setRcAuthed} rcStatus={rcStatus}/> }

      {/* Brand compliance footer — Equal Housing · NMLS · licensed states */}
      <footer style={{marginTop:48,paddingTop:20,borderTop:"1px solid "+C.rule,display:"flex",justifyContent:"center"}}>
        <ComplianceBar tone="navy" size={9}/>
      </footer>
      </div>}
    </main>

    {/* Pipeline automations slide-out panel */}
    {autoPanelOpen&&access.pipeline&&!ownOnly&&<div onClick={()=>setAutoPanelOpen(false)} style={{position:"fixed",inset:0,zIndex:210,background:"rgba(6,26,48,0.55)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)"}}>
      <aside onClick={e=>e.stopPropagation()} style={{position:"absolute",right:0,top:0,bottom:0,width:"min(720px,92vw)",background:C.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",borderLeft:"1px solid "+C.glassBorder,boxShadow:"-2px 0 40px rgba(11,42,74,0.16)",overflowY:"auto",animation:"slideInR 0.2s cubic-bezier(.2,.8,.2,1)"}}>
        <div style={{position:"sticky",top:0,padding:"14px 24px",background:C.glass,backdropFilter:"blur(14px)",WebkitBackdropFilter:"blur(14px)",borderBottom:"1px solid "+C.glassBorder,display:"flex",alignItems:"center",justifyContent:"space-between",zIndex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}><span style={{color:C.green}}>{Ic.auto}</span><span style={{fontSize:14,fontWeight:700}}>Automations</span></div>
          <button onClick={()=>setAutoPanelOpen(false)} style={{width:30,height:30,borderRadius:8,border:"1px solid "+C.border,background:"transparent",color:C.textMuted,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{padding:"20px 24px"}}>
          <AutoHub loans={loans} sel={sel} setSel={setSel} to={to} setTo={setTo}/>
        </div>
      </aside>
      <style>{`@keyframes slideInR{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>
    </div>}

    {inboundCall&&access.newCall&&<InboundCallToast call={inboundCall}
      onDismiss={()=>setInboundCall(null)}
      onOpenLoan={function(id){var l=loans.find(function(x){return x.id===id;});if(l){setSel(l);setNav("loanDetail");}setInboundCall(null);}}
      onOpenLead={function(){setNav("marketing");setInboundCall(null);}}
      /* Take the call: open the console straight on the caller — matched to
         their file/lead if we know them, otherwise pre-searched on the number
         so he can find them by address or spelling instead of guessing. */
      onTakeCall={function(){
        var c=inboundCall;var seed={phone:c.from||"",callEpoch:c.epoch||null};
        if(c.match&&c.match.kind==="loan"){
          var l=loans.find(function(x){return x.id===c.match.id;});
          if(l)seed={prospect:prospectFromLoan(l),callEpoch:c.epoch||null};
        }else if(c.match&&c.match.kind==="lead"){
          var m=mktgLeads.find(function(x){return x.id===c.match.id;});
          if(m)seed={prospect:prospectFromLead(m),callEpoch:c.epoch||null};
        }
        setInboundCall(null);startCall(seed);
      }}/>}

    {/* ── New Call console — full-screen overlay, available from every view.
        dockLeft / dockRight: the Whiteboard / chat bubble floats above it in
        that bottom corner, so its sticky action bar keeps the corner clear. ── */}
    {access.newCall&&<CallConsole open={callOpen} onClose={endCall} staff={staffKey}
      loans={loans} setLoans={setLoans}
      mktgLeads={mktgLeads} setMktgLeads={setMktgLeads}
      leadsReady={leadsReady} leadLoadProgress={leadLoadProgress}
      allLeadsLoaded={allLeadsLoaded} adoptLeads={adoptLeads}
      hecmLimit={settings.hecmLimit} rateSheets={settings.rateSheets}
      setRateSheets={rs=>updateSettings({rateSheets:rs})}
      setNav={setNav} onOpenFile={openPitchedFile} seed={callSeed} onRegisterFlush={regCallFlush}
      dockLeft={user==="admin"&&!!access.whiteboard} dockRight={user==="admin"&&!!access.library}
      settings={settings} rcAuthed={rcAuthed} practice={practice}/>}

    {/* ── Ask the Library, from every screen — a chat button in the
        bottom-right corner that opens a small chat box (it floats above the
        New Call console too). The SAME conversation as the Library page's
        Ask tab (one per person — src/libraryChat.js), so it's hidden on the
        Library screen itself, unless the New Call console is open over it;
        also while the menu or the automations panel is open. Staff only (the
        chatbot's proxy rejects anyone else) — never the borrower portal or
        the public pages, which return before this shell renders. */}
    {user==="admin"&&access.library&&<ChatDock key={staffKey+(practice?":practice":"")} staff={staffKey} practice={practice}
      hidden={(view==="library"&&!callOpen)||navOpen||autoPanelOpen}
      libraryLocked={callOpen}
      onOpenLibrary={openLibraryFromDock}/>}

    {/* ── The Whiteboard, from every screen — a note bubble in the
        bottom-LEFT corner (the chat's mirror image) that opens Reece's misc
        notes. The SAME board as the Command Center's Whiteboard panel, so it's
        hidden on the Command Center itself unless the New Call console is open
        over it (the console's action bar keeps its corner clear — dockLeft
        above); also while the menu or the automations panel is open. On the
        Library screen it sits just above that page's Ask box (however tall —
        --lf-ask-h) and under the Library's viewer / modals. Mounted after the
        chat so an open whiteboard panel paints over the chat bubble (and vice
        versa via z-index — see src/ui.jsx useDockRoom). */}
    {user==="admin"&&access.whiteboard&&<WhiteboardDock staff={staffKey}
      hidden={(view==="dashboard"&&!callOpen)||navOpen||autoPanelOpen}
      lifted={view==="library"&&!callOpen}/>}

    {/* Outcome of the last call — confirms where it landed after the overlay
        closes. Kept clear of the two corner bubbles (56px + 20px gutter each
        side), so on a phone it wraps between them instead of under them. */}
    {callFlash&&<div style={{position:"fixed",bottom:20,left:"50%",transform:"translateX(-50%)",zIndex:380,padding:"12px 22px",borderRadius:26,background:C.navyDeep,color:C.cream,fontSize:13,fontWeight:700,boxShadow:"0 8px 30px rgba(6,26,48,0.35)",maxWidth:"min(90vw, calc(100vw - 176px))",boxSizing:"border-box",textAlign:"center"}}>{callFlash}</div>}
  </div></OwnershipUiProvider>);
}


